import { toApiExecutionCandleType } from "../../../utils/executionCandleTimeframe";
import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Flex } from "@chakra-ui/react";
import { createPortal } from "react-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { statsCardHeadlineClassName } from "../Statistics/components/statisticsCardHeadline";
import { AlertTriangle, Calendar, List as ListIcon, CandlestickChart, ChartCandlestick, Code2, Crosshair, Filter, History, Loader2, Settings, SlidersHorizontal } from "lucide-react";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { STRATEGY_PINE_SECTION_LABEL_CLASS } from "@/components/Strategies/StrategyPineSectionCard";
import { ResearchDisclaimerNotice } from "@/components/ResearchDisclaimerNotice";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useStrategyRdTimezone } from "./useStrategyRdTimezone";
import { strategyRdMemo, StrategyRdWhenVisible } from "./strategyRdMemo";
import { useOpenStrategyReport } from "../Workspace/useOpenStrategyReport";
import { cn } from "@/lib/utils";
import {
  applyStrategyRdHypothesis,
  confirmStrategyRdHypothesis,
  createStrategyRdHypothesis,
  fetchStrategyRdCandles,
  fetchStrategyRdRuleVocabulary,
  getStrategyRdHypothesis,
  isStrategyRdDisabledError,
  isStrategyRdHypothesisLimitError,
  listAllStrategyRdHypothesisTrades,
  listStrategyRdCatalog,
  listStrategyRdInstruments,
  previewStrategyRdConcepts,
  previewStrategyRdPath,
  parseStrategyRdRuleTemplate,
  parseStrategyRdSizing,
  resolveStrategyRd,
  scanStrategyRd,
  type StrategyRdRuleComposer,
} from "../../../services/strategyRdApi";
import {
  strategyRdConfirmCoachTemplate,
  strategyRdDeepMergePatch,
  strategyRdRuleFieldErrors,
  strategyRdTemplateEntryFromComposerMode,
  type StrategyRdRuleFieldErrors,
} from "../../../utils/strategyRdRuleEditor";
import { quoteVariantSelectOptions } from "../../../utils/instrumentSetupSave";
import { presentMutationError, toastError } from "../../../utils/showApiErrorToast";
import { strategyRdWaveCIsRsId } from "../../../utils/strategyRdWaveCLocation";
import { strategyRdStatsWithSeriesBarTimes } from "../../../utils/strategyRdIndicatorSeries";
import {
  saveStrategyRdPineExportInfo,
  strategyRdPineChartTimeframeHint,
  strategyRdPineNotTranslatableMissing,
} from "../../../utils/strategyRdPineExport";
import {
  clampStrategyRdLookbackDays,
  strategyRdChartStartMs,
  STRATEGY_RD_CHART_BARS,
  defaultStrategyRdRuleTemplate,
  isStrategyRdQuoteVariantRequired,
  isValidStrategyRdSizing,
  strategyRdConfirmDefaultName,
  strategyRdHasSizedApply,
  strategyRdHypothesisName,
  strategyRdLookbackCapDays,
  strategyRdMatchKey,
  strategyRdQuoteVariantsFromError,
  strategyRdResolvedSymbolForRequest,
  strategyRdSizingForWire,
  strategyRdStopOffsetNeedsValue,
  withResolvedStrategyRdUnitSizes,
  STRATEGY_RD_DEFAULT_CANDLE_TYPE,
  STRATEGY_RD_DEFAULT_LOOKBACK_DAYS,
} from "../../../utils/strategyRd";
import {
  applyStrategyRdPackRecipe,
  normalizeStrategyRdSessionIdsSelection,
  STRATEGY_RD_SESSION_ALL,
  STRATEGY_RD_SR_SCOPES,
  strategyRdConfluenceDroppedDisplay,
  strategyRdConfluenceSkippedDisplay,
  strategyRdConfluenceSkippedReasons,
  strategyRdConfluenceSoftPassNotes,
  strategyRdHasClearOfOpposingSr,
  strategyRdMatchSessionId,
  strategyRdSessionDisplayName,
  strategyRdSessionPickerGroups,
  strategyRdRewriteSlotToPicker,
  parseStrategyRdEntryMode,
  strategyRdSetupNeedsSession,
  strategyRdSrScopesForRequest,
  strategyRdVolumeAvailableFromStats,
} from "../../../utils/strategyRdConcepts";
import {
  normalizeStrategyRdSlotSide,
  compactNestedComposerFields,
  strategyRdAlignEntryToSetup,
  strategyRdAlignEntrySideToSetup,
  strategyRdComposerTouchKeys,
  strategyRdConfluenceIdsForChartTf,
  strategyRdConfluenceSlotsForRequest,
  strategyRdDefaultConfluenceSide,
  strategyRdDefaultSetupSide,
  strategyRdInstrumentBrokerPins,
  strategyRdNestedComposerFingerprint,
  strategyRdEntryWithMaxWait,
  strategyRdOptionalMinForWire,
  strategyRdSidePolicyFromSlotSide,
} from "../../../utils/strategyRdComposer";
import {
  strategyRdApplyBuiltConfluenceSlot,
  strategyRdApplyBuiltSetupSlot,
  strategyRdBuildSlotFromSettings,
  strategyRdDefaultSettingUiValues,
  strategyRdMergeComposerEchoInteractions,
} from "../../../utils/strategyRdSettings";
import {
  mergeStrategyRdPreviewPaths,
  strategyRdMatchDisplayDirection,
  strategyRdMatchEntryBarTime,
  strategyRdPathAppliedLevels,
  strategyRdPathOrderLines,
  strategyRdPathInvalidReasonLabel,
  strategyRdIncompleteEntryMessage,
  strategyRdShouldShowIncompleteEntryBanner,
  takeStrategyRdPreviewExamples,
} from "../../../utils/strategyRdPath";
import {
  strategyRdDetectFilterCountLabel,
  strategyRdDetectWindowCandles,
  strategyRdDetectWindowKey,
  strategyRdWarmCandlesForRequest,
  resolveStrategyRdMatchMarkers,
} from "../../../utils/strategyRdDetect";
import type { QuoteVariantCandidate } from "../../../types/instrumentSetup";
import type {
  StrategyRdApplyOverview,
  StrategyRdApplyResponse,
  StrategyRdApplyResult,
  StrategyRdCandleType,
  StrategyRdCatalog,
  StrategyRdComposerConfluenceSlot,
  StrategyRdComposerEntrySlot,
  StrategyRdComposerSetupSlot,
  StrategyRdEntryGuidance,
  StrategyRdEntryMode,
  StrategyRdEntryRuleSpec,
  StrategyRdFragileTrade,
  StrategyRdHypothesis,
  StrategyRdInstrument,
  StrategyRdLogicPath,
  StrategyRdMatch,
  StrategyRdMatchMarker,
  StrategyRdOhlcBar,
  StrategyRdOhlcStats,
  StrategyRdOutcomeSummary,
  StrategyRdPack,
  StrategyRdResolveResponse,
  StrategyRdRobustness,
  StrategyRdRuleRole,
  StrategyRdRuleTemplate,
  StrategyRdRuleVocabulary,
  StrategyRdSidePolicy,
  StrategyRdSizing,
  StrategyRdSrRail,
} from "../../../types/strategyRd";
import {
  DEFAULT_STRATEGY_RD_SIZING,
  DEFAULT_STRATEGY_RD_STARTING_EQUITY,
  STRATEGY_RD_CANDLE_TYPES,
} from "../../../types/strategyRd";
import {
  previewStrategyRdLevels,
  strategyRdExitLegMarkers,
  strategyRdExitReason,
  strategyRdStopPathSteps,
  strategyRdMatchesExecutionOverlays,
} from "../../../utils/strategyRdRules";
import { StatisticsEmptyState } from "../Statistics/components/statisticsEmptyState";
import {
  StrategyRdTradesCard,
  strategyRdHasSideViews,
  strategyRdSideOverview,
  strategyRdSideResults,
  type StrategyRdSideView,
} from "./StrategyRdApplyTable";
import {
  StrategyRdLabOverview,
  StrategyRdLabResultsHeader,
  type StrategyRdLabResultsTab,
  type StrategyRdResultsMeta,
} from "./StrategyRdLabResults";
import {
  strategyRdConditionPickLabel,
  strategyRdConditionSetupBlockingReason,
  strategyRdIsConditionSetup,
  strategyRdKeepSetupConditions,
  strategyRdSetupConditions,
} from "../../../utils/strategyRdConditionSetup";
import { StrategyRdComposer } from "./StrategyRdComposer";
import {
  readCandleScheme,
  readLegendHidden,
  STRATEGY_RD_CANDLE_SCHEMES,
  StrategyRdDetectChart,
  writeCandleScheme,
  writeLegendHidden,
  type StrategyRdCandleSchemeId,
} from "./StrategyRdDetectChart";
import { StrategyRdMatchRail } from "./StrategyRdMatchRail";
import { StrategyRdRobustnessPanel } from "./StrategyRdRobustnessPanel";
import { StrategyRdAccountPanel } from "./StrategyRdAccountPanel";
import { StrategyRdMarketsPanel } from "./StrategyRdMarketsPanel";
import { StrategyRdSavedBacktestsDialog, StrategyRdVersionsPanel } from "./StrategyRdVersionsPanel";
import { parseCoachComposerSlots } from "../../../utils/strategyRdCoachContext";
import { StrategyRdRulesPanel, strategyRdRulesBlockingReason } from "./StrategyRdRulesPanel";
import {
  StrategyRdDockHint,
  StrategyRdChartStack,
  StrategyRdLabDrawer,
  StrategyRdLabTaskbar,
  StrategyRdLabTopBarActions,
  type StrategyRdLabPanel,
  StrategyRdQuoteSelect,
  StrategyRdSettingSwitch,
  StrategyRdCandleColorPicker,
  StrategyRdTimeZoneToggle,
  StrategyRdWindowPicker,
  type StrategyRdDateRange,
  strategyRdWindowLabel,
  StrategyRdResultsDock,
  useFillViewportHeight,
} from "./StrategyRdLabLayout";
import { StrategyRdPinePanel } from "./StrategyRdPineDialog";
import type { StrategyRdRuleStatus } from "./StrategyRdRuleEditor";
import {
  strategyRdBrandActionButtonClassName,
  strategyRdMetaClassName,
  strategyRdSectionTitleClassName,
} from "./strategyRdCardShell";
import {
  StrategyRdCoachRail,
  StrategyRdCoachPanel,
  useStrategyRdCoach,
  type StrategyRdCoachLabApply,
} from "./coach";

const FlexAny = Flex as any;

function coverageLabel(stats: StrategyRdOhlcStats | null): string | null {
  if (!stats) return null;
  const scanned = typeof stats.bars_scanned === "number" ? stats.bars_scanned : null;
  const expected = typeof stats.bars_expected === "number" ? stats.bars_expected : null;
  if (scanned == null) return null;
  if (expected != null && expected > 0) {
    return `${scanned} / ${expected} bars`;
  }
  return `${scanned} bars scanned`;
}


/** Backtest notes that compare with TradingView: shown in the Pine Script dialog only. */
const STRATEGY_RD_TRADINGVIEW_NOTE_CODES = new Set(["feed_approximate", "tv_counts_partials", "bar_magnifier_approx"]);

/** The taskbar on the right edge of the Lab. */
const LAB_TASKBAR_WIDTH = 48;
/** Below this the chart toolbar and the results tabs no longer fit. */
const LAB_CHART_MIN_COMFORT = 520;

/** Every plan opens the Lab (Free with lower limits); the backend enforces the plan per action. */
export default function StrategyRdPage(): JSX.Element {
  return <StrategyRdPageBody />;
}

// Heavy sections re-render only when their data changes (see strategyRdMemo).
const LabOverviewSection = strategyRdMemo(StrategyRdLabOverview);
const TradesSection = strategyRdMemo(StrategyRdTradesCard);
const MatchRailSection = strategyRdMemo(StrategyRdMatchRail, ["sessionLabelFor"]);
const CoachPanelSection = strategyRdMemo(StrategyRdCoachPanel);

const LAB_TZ_MODE_KEY = "strategyRd.lab.timeZoneMode";
const LAB_LIVE_PREVIEW_KEY = "strategyRd.lab.livePreview";

function StrategyRdPageBody(): JSX.Element {
  const history = useHistory();
  const shadcnInset = useShadcnInsetChrome();
  const timezone = useStrategyRdTimezone();
  const localTimeZone = timezone?.trim() || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  /** §11.3: one display zone for the chart, lists and TES cards — local by default, or UTC. */
  const [timeZoneMode, setTimeZoneMode] = useState<"local" | "utc">(() => {
    try {
      return window.localStorage.getItem(LAB_TZ_MODE_KEY) === "utc" ? "utc" : "local";
    } catch {
      return "local";
    }
  });
  const displayTimeZone = timeZoneMode === "utc" ? "UTC" : localTimeZone;
  useEffect(() => {
    try {
      window.localStorage.setItem(LAB_TZ_MODE_KEY, timeZoneMode);
    } catch {
      // private mode — the choice lasts this visit
    }
  }, [timeZoneMode]);

  const [catalog, setCatalog] = useState<StrategyRdCatalog | null>(null);
  const sessionPickerGroups = useMemo(() => strategyRdSessionPickerGroups(catalog), [catalog]);
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);

  const [instruments, setInstruments] = useState<StrategyRdInstrument[]>([]);
  const [instrumentsLoading, setInstrumentsLoading] = useState(false);
  /** Every Lab market, for the Other markets picker. */
  const marketSymbols = useMemo(
    () => Array.from(new Set(instruments.map((row) => row.display_symbol.trim().toUpperCase()).filter(Boolean))).sort(),
    [instruments]
  );
  const [selectedInstrumentId, setSelectedInstrumentId] = useState("");
  const [instrumentStepConfirmed, setInstrumentStepConfirmed] = useState(false);

  const [symbol, setSymbol] = useState("");
  const [assetClass, setAssetClass] = useState<string>("");
  const [candleType, setCandleType] = useState<StrategyRdCandleType>(STRATEGY_RD_DEFAULT_CANDLE_TYPE);
  const [setupSlot, setSetupSlot] = useState<StrategyRdComposerSetupSlot>({ id: "" });
  const [composerFlash, setComposerFlash] = useState<{ token: number; keys: string[] }>({
    token: 0,
    keys: [],
  });
  const [confluenceSlots, setConfluenceSlots] = useState<StrategyRdComposerConfluenceSlot[]>([]);
  const [entrySlot, setEntrySlot] = useState<StrategyRdComposerEntrySlot>({
    mode: "signal_close",
    side: "both",
  });
  const [srScopes, setSrScopes] = useState<string[]>([...STRATEGY_RD_SR_SCOPES]);
  const [sessionIds, setSessionIds] = useState<string[]>([STRATEGY_RD_SESSION_ALL]); // every hour unless a session is picked
  const [activePackId, setActivePackId] = useState<string | null>(null);
  const [lookbackDays, setLookbackDays] = useState(STRATEGY_RD_DEFAULT_LOOKBACK_DAYS);
  /** A specific period ("Mar 1 – Jun 30"); null = the last `lookbackDays` up to now. */
  const [dateRange, setDateRange] = useState<StrategyRdDateRange | null>(null);
  /** The saved backtest last opened, for its name in the results header. */
  const [openedBacktest, setOpenedBacktest] = useState<{ id: string; name: string } | null>(null);
  const dateRangeBody = dateRange ? { start_date: dateRange.start, end_date: dateRange.end } : {};
  const [logicPath, setLogicPath] = useState<StrategyRdLogicPath | null>(null);
  const [entryGuidance, setEntryGuidance] = useState<StrategyRdEntryGuidance | null>(null);
  /** Live preview examples only (0–20). Apply uses Scan `matches`, never this. */
  const [previewMatches, setPreviewMatches] = useState<StrategyRdMatch[]>([]);
  /** Setups found: closed by default; opens over the chart, never beside it. */
  /** The panel between the chart and the taskbar: setups (top bar), Pine editor or chart settings. */
  const [labPanel, setLabPanel] = useState<StrategyRdLabPanel | null>(null);
  const toggleLabPanel = useCallback((panel: StrategyRdLabPanel) => {
    setLabPanel((prev) => {
      if (prev === panel) return null;
      setSettingsPanel(null);
      return panel;
    });
  }, []);
  const [candleScheme, setCandleScheme] = useState(readCandleScheme);
  const onCandleSchemeChange = useCallback((id: StrategyRdCandleSchemeId) => {
    setCandleScheme(id);
    writeCandleScheme(id);
  }, []);
  const [legendHidden, setLegendHidden] = useState(readLegendHidden);
  const onLegendHiddenChange = useCallback((hidden: boolean) => {
    setLegendHidden(hidden);
    writeLegendHidden(hidden);
  }, []);
  const [resultsTab, setResultsTab] = useState<StrategyRdLabResultsTab>("overview");
  const [resultsSide, setResultsSide] = useState<StrategyRdSideView>("combined");
  const [previewCandles, setPreviewCandles] = useState<StrategyRdOhlcBar[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCommitted, setPreviewCommitted] = useState(false);
  const [previewOhlcStats, setPreviewOhlcStats] = useState<StrategyRdOhlcStats | null>(null);
  const [previewEmptyState, setPreviewEmptyState] = useState<
    import("@/types/strategyRd").StrategyRdPreviewEmptyState | null
  >(null);
  const [previewMarkers, setPreviewMarkers] = useState<StrategyRdMatchMarker[]>([]);
  const [previewSrRails, setPreviewSrRails] = useState<StrategyRdSrRail[]>([]);
  const [resolvedSymbol, setResolvedSymbol] = useState("");
  const [quoteCandidates, setQuoteCandidates] = useState<QuoteVariantCandidate[]>([]);
  const [listing, setListing] = useState<StrategyRdResolveResponse | null>(null);

  const [scanning, setScanning] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState<string | null>(null);
  const [matches, setMatches] = useState<StrategyRdMatch[]>([]);
  const [ohlcStats, setOhlcStats] = useState<StrategyRdOhlcStats | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  /** Rail/table selection zooms the chart; chart clicks only change selection. */
  const [focusMatchToken, setFocusMatchToken] = useState<string | null>(null);
  const [hasScanned, setHasScanned] = useState(false);
  const [scanCandles, setScanCandles] = useState<StrategyRdOhlcBar[]>([]);
  /** Set when the scan's window is longer than the chart holds: Apply / path preview use it. */
  const [scanJobId, setScanJobId] = useState<string | null>(null);
  const scanJobIdRef = useRef<string | null>(null);
  scanJobIdRef.current = scanJobId;
  const [scanMarkers, setScanMarkers] = useState<StrategyRdMatchMarker[]>([]);
  const [scanSrRails, setScanSrRails] = useState<StrategyRdSrRail[]>([]);
  const [ruleTemplate, setRuleTemplate] = useState<StrategyRdRuleTemplate>(
    defaultStrategyRdRuleTemplate
  );
  const [sidePolicy, setSidePolicy] = useState<StrategyRdSidePolicy>("both");
  const [startingEquity, setStartingEquity] = useState(DEFAULT_STRATEGY_RD_STARTING_EQUITY);
  const [sizing, setSizing] = useState<StrategyRdSizing>(DEFAULT_STRATEGY_RD_SIZING);
  const [hypothesisId, setHypothesisId] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyStatus, setApplyStatus] = useState<string | null>(null);
  const [applyResults, setApplyResults] = useState<StrategyRdApplyResult[]>([]);
  const [applyOverview, setApplyOverview] = useState<StrategyRdApplyOverview | null>(null);
  /** Account-level Apply echo: backtest stamp, leverage used, margin, currency, parity. */
  const [applyMeta, setApplyMeta] = useState<Pick<
    StrategyRdApplyResponse,
    "applied_at" | "sizing" | "margin" | "currency" | "parity" | "warnings"
  > | null>(null);
  const [fragileTrades, setFragileTrades] = useState<StrategyRdFragileTrade[]>([]);
  /** Finished robustness result for the current backtest (null = none yet). */
  const [robustnessResult, setRobustnessResult] = useState<StrategyRdRobustness | null>(null);
  /** Bumped by warning actions / the Save dialog to start the robustness test. */
  const [robustnessRunToken, setRobustnessRunToken] = useState(0);
  const [settingFocus, setSettingFocus] = useState<{ field: string; token: number } | null>(null);
  const [checkFirstOpen, setCheckFirstOpen] = useState(false);
  /** Strategy settings: one side drawer with two tabs — Recipe and Backtest. */
  const [settingsPanel, setSettingsPanel] = useState<"recipe" | "backtest" | null>(null);
  /** The settings tab the cog reopens (Recipe until Backtest was last used). */
  const lastSettingsTabRef = useRef<"recipe" | "backtest">("recipe");
  useEffect(() => {
    if (settingsPanel) lastSettingsTabRef.current = settingsPanel;
  }, [settingsPanel]);
  // One panel beside the chart at a time: opening the settings closes the others.
  const setRecipeOpen = useCallback((open: boolean) => {
    if (open) setLabPanel(null);
    setSettingsPanel((p) => (open ? "recipe" : p === "recipe" ? null : p));
  }, []);
  const setSettingsOpen = useCallback((open: boolean) => {
    if (open) setLabPanel(null);
    setSettingsPanel((p) => (open ? "backtest" : p === "backtest" ? null : p));
  }, []);
  /** Width of the open drawer — the page makes room for it so the chart stays in view. */
  const [settingsDrawerWidth, setSettingsDrawerWidth] = useState(0);
  const [labFillRef, labHeight] = useFillViewportHeight(12);
  const [saveOpen, setSaveOpen] = useState(false);
  /** Backtest history → Save as strategy on one that isn't open: open it, then the save dialog. */
  const [pendingSaveId, setPendingSaveId] = useState<string | null>(null);
  // After a save: stay in the Lab, or go to the strategy (it used to jump there straight away).
  const [savedInfo, setSavedInfo] = useState<{ name: string; deepLink: string | null; notes?: string[] } | null>(null);
  const { openReport, opening: openingReport } = useOpenStrategyReport();
  /** Template the open backtest ran with — Pine is stale once the page's rules differ. */
  const [appliedTemplateKey, setAppliedTemplateKey] = useState<string | null>(null);
  /** N of M: at least this many optional confluences must pass. */
  const [optionalMin, setOptionalMin] = useState(1);
  const [ruleVocabulary, setRuleVocabulary] = useState<StrategyRdRuleVocabulary | null>(null);
  /** Compile state of the active custom rules (from the Rules panel). */
  const [ruleStatuses, setRuleStatuses] = useState<
    Partial<Record<StrategyRdRuleRole, StrategyRdRuleStatus>>
  >({});
  /** Apply / Save errors that belong to a rule field. */
  const [ruleFieldErrors, setRuleFieldErrors] = useState<StrategyRdRuleFieldErrors | null>(null);
  /** Composer entry mode to restore when Rules → Entry goes back from a custom rule. */
  const presetEntryModeRef = useRef<string>("signal_close");
  /** Bumped when versions may have changed (a backtest ran, TES finished a run, a delete). */
  const [versionsRefresh, setVersionsRefresh] = useState(0);
  const [savedBacktestsOpen, setSavedBacktestsOpen] = useState(false);
  // The library is always one click away (top bar), not only once a backtest is open.
  // Lab actions sit in the app's top bar, portalled so their busy / count / disabled state stays live.
  const { setActions: setInsetHeaderActions, mountEl: insetHeaderMount } = useShadcnInsetHeaderActions();
  useEffect(() => {
    setInsetHeaderActions(null);
  }, [setInsetHeaderActions]);
  /** "Showing the first 100 trades" when the full trade list isn't stored. */
  const [tradesNote, setTradesNote] = useState<string | null>(null);
  /** Open in Lab: the hypothesis whose results are restored once its scan finishes. */
  const pendingOpenRef = useRef<StrategyRdHypothesis | null>(null);
  /** The last backtest opened: its version and whether it's saved, for TES's actions on it. */
  const openedInfoRef = useRef<{ id: string; version: number | null; saved: boolean } | null>(null);
  const restoreOpenedRef = useRef<((h: StrategyRdHypothesis) => void) | null>(null);
  const [openScanToken, setOpenScanToken] = useState(0);
  const loadAllTradesRef = useRef<((hid: string, shown: number) => Promise<void>) | null>(null);
  /** Run backtest = find this window's setups (the old Scan), then backtest them. */
  const applyAfterScanRef = useRef(false);
  const [applyToken, setApplyToken] = useState(0);
  /** Saved robustness of an opened backtest (the panel's initial result). */
  const [openedRobustness, setOpenedRobustness] = useState<StrategyRdRobustness | null>(null);
  /** The opened backtest's saved "other markets" table (raw `last_markets`). */
  const [openedMarkets, setOpenedMarkets] = useState<unknown>(null);
  const [reportStrategyHint, setReportStrategyHint] = useState<{
    report_strategy_id?: string | null;
    deep_link?: string | null;
  } | null>(null);
  const [confirmName, setConfirmName] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<string | null>(null);
  const [coachKickPreview, setCoachKickPreview] = useState(0);
  const coachHostRef = useRef<HTMLDivElement | null>(null);

  const scanAbortRef = useRef<AbortController | null>(null);
  const scanAfterVariantRef = useRef(false);
  const previewAbortRef = useRef<AbortController | null>(null);
  const previewSkipRef = useRef(false);
  const conceptsPreviewAbortRef = useRef<AbortController | null>(null);
  const previewFingerprintRef = useRef<string>("");
  /** Live preview: re-preview the chart after each recipe edit (per-browser preference). */
  const [livePreview, setLivePreview] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(LAB_LIVE_PREVIEW_KEY) !== "off";
    } catch {
      return true;
    }
  });
  const onLivePreviewChange = useCallback((on: boolean) => {
    setLivePreview(on);
    try {
      window.localStorage.setItem(LAB_LIVE_PREVIEW_KEY, on ? "on" : "off");
    } catch {
      /* preference only */
    }
  }, []);
  /** The recipe the last live preview ran for — one try per recipe, even if it failed. */
  const livePreviewFpRef = useRef("");
  const matchesRef = useRef(matches);
  const scanCandlesRef = useRef(scanCandles);
  const previewMatchesRef = useRef(previewMatches);
  const previewCandlesRef = useRef(previewCandles);
  /** Skip preference auto-window after the user clicks Change market. */
  const marketWindowTouchedRef = useRef(false);
  const ruleTemplateRef = useRef(ruleTemplate);
  const setupSlotRef = useRef(setupSlot);
  setupSlotRef.current = setupSlot;
  matchesRef.current = matches;
  scanCandlesRef.current = scanCandles;
  ruleTemplateRef.current = ruleTemplate;
  /** A scan / backtest is running — TES runs don't take over the Lab meanwhile. */
  const labBusyRef = useRef(false);
  labBusyRef.current = scanning || applying;
  const hypothesisIdRef = useRef(hypothesisId);
  hypothesisIdRef.current = hypothesisId;
  const sizingRef = useRef(sizing);
  sizingRef.current = sizing;
  /** Latest Entry slot for async effects (Max wait on path repricing). */
  const entrySlotRef = useRef(entrySlot);
  entrySlotRef.current = entrySlot;
  previewMatchesRef.current = previewMatches;
  previewCandlesRef.current = previewCandles;

  const lookbackCap = strategyRdLookbackCapDays(candleType);
  const setupId = setupSlot.id;
  const confluences = confluenceSlots.map((c) => c.id);
  /** Catalog names for the chart's legend groups (same names as the composer pickers). */
  const chartConceptLabel = useCallback(
    (id: string) => catalog?.concepts?.find((c) => c.id === id)?.name?.trim() || null,
    [catalog]
  );
  const confluenceRefsForChart = useMemo(
    () =>
      confluenceSlots.map((c) => ({
        id: c.id,
        candle_type: c.candle_type || candleType,
      })),
    [candleType, confluenceSlots]
  );
  /** Continuous MA lines only when the confluence TF matches the chart series. */
  const laneBConceptIds = useMemo(
    () => strategyRdConfluenceIdsForChartTf(confluenceSlots, candleType, candleType),
    [candleType, confluenceSlots]
  );
  const selectedInstrument =
    instruments.find((row) => row.instrument_id === selectedInstrumentId) || null;
  const instrumentBrokerPins = strategyRdInstrumentBrokerPins(selectedInstrument);
  useEffect(() => {
    if (entrySlot.mode !== "signal_open") return;
    setEntrySlot((prev) =>
      prev.mode === "signal_open" ? { ...prev, mode: "next_open" } : prev
    );
  }, [entrySlot.mode]);
  const detectMatches = useMemo(
    () => (hasScanned ? matches : previewMatches),
    [hasScanned, matches, previewMatches]
  );
  const detectMarkers = hasScanned ? scanMarkers : previewMarkers;
  const detectSrRails = hasScanned ? scanSrRails : previewSrRails;
  const windowCandles =
    scanCandles.length > 0 ? scanCandles : previewCandles;
  const selectedMatch = useMemo(() => {
    const rows = hasScanned ? matches : previewMatches;
    return rows.find((m) => strategyRdMatchKey(m) === selectedKey) ?? rows[0] ?? null;
  }, [hasScanned, matches, previewMatches, selectedKey]);
  const optionalMinWire = useMemo(
    () => strategyRdOptionalMinForWire(confluenceSlots, optionalMin),
    [confluenceSlots, optionalMin]
  );
  const entryRuleForScan = ruleTemplate.entry.mode === "rule" ? ruleTemplate.entry.rule ?? null : null;
  const entryFilterForScan = ruleTemplate.entry.filter ?? null;
  const recipeFingerprint = useMemo(
    () =>
      strategyRdNestedComposerFingerprint({
        setup: setupSlot,
        confluences: confluenceSlots,
        entry: entrySlot,
        sessionIds,
        srScopes,
        setupCandleType: candleType,
        optionalMin: optionalMinWire ?? null,
        entryRule: entryRuleForScan,
        entryFilter: entryFilterForScan,
      }),
    [
      candleType,
      confluenceSlots,
      entryFilterForScan,
      entryRuleForScan,
      entrySlot,
      optionalMinWire,
      sessionIds,
      setupSlot,
      srScopes,
    ]
  );
  /** The recipe custom rules are checked against (`/rules/vocabulary`, `/rules/compile`). */
  const ruleComposer = useMemo<StrategyRdRuleComposer | null>(() => {
    if (!setupSlot.id.trim()) return null;
    const nested = compactNestedComposerFields({
      setup: setupSlot,
      confluences: confluenceSlots,
      entry: entrySlot,
      setupCandleType: candleType,
    });
    return {
      candle_type: candleType,
      setup: nested.setup ?? { id: setupSlot.id },
      confluences: nested.confluences ?? [],
      ...(nested.entry ? { entry: nested.entry } : {}),
      ...(optionalMinWire ? { optional_min: optionalMinWire } : {}),
    };
  }, [candleType, confluenceSlots, entrySlot, optionalMinWire, setupSlot]);
  const ruleComposerKey = useMemo(() => JSON.stringify(ruleComposer), [ruleComposer]);
  useEffect(() => {
    if (!ruleComposer) {
      setRuleVocabulary(null);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      fetchStrategyRdRuleVocabulary(ruleComposer, controller.signal)
        .then(setRuleVocabulary)
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === "AbortError") return;
          // Custom rules stay unavailable; presets keep working.
          setRuleVocabulary(null);
        });
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // ruleComposerKey stands in for ruleComposer.
  }, [ruleComposerKey]);
  /** A failing / unchecked entry rule or filter changes the hits — Scan and Preview wait. */
  const scanRulesBlocking = strategyRdRulesBlockingReason({
    entry: ruleStatuses.entry,
    filter: ruleStatuses.filter,
  });
  const scanRulesBlockingRef = useRef(scanRulesBlocking);
  scanRulesBlockingRef.current = scanRulesBlocking;
  /** Stop / target rules reprice through the BE — not while one fails or is being checked. */
  const priceRulesReady = !strategyRdRulesBlockingReason({
    stop: ruleStatuses.stop,
    target: ruleStatuses.target,
  });
  const onEntryRuleChange = useCallback(
    (rule: StrategyRdEntryRuleSpec | null) => {
      if (rule) {
        if (entrySlot.mode !== "rule") presetEntryModeRef.current = entrySlot.mode || "signal_close";
        setEntrySlot((prev) => (prev.mode === "rule" ? prev : { ...prev, mode: "rule" }));
        setRuleTemplate((prev) => ({ ...prev, entry: { ...prev.entry, mode: "rule", rule } }));
        return;
      }
      const mode = presetEntryModeRef.current || "signal_close";
      setEntrySlot((prev) => ({ ...prev, mode }));
      setRuleTemplate((prev) => {
        const { rule: _drop, ...entry } = prev.entry;
        return { ...prev, entry: { ...entry, mode: mode as StrategyRdEntryMode } };
      });
    },
    [entrySlot.mode]
  );
  const sessionLabelForMatch = useCallback(
    (match: StrategyRdMatch) => {
      const sid = strategyRdMatchSessionId(match.meta);
      if (!sid) return null;
      return strategyRdSessionDisplayName(catalog, sid) || sid;
    },
    [catalog]
  );
  const unitOpts = useMemo(
    () => ({
      symbol: listing?.resolved_symbol || resolvedSymbol || symbol,
      assetClass: listing?.asset_class || assetClass,
    }),
    [assetClass, listing?.asset_class, listing?.resolved_symbol, resolvedSymbol, symbol]
  );
  /** Live Entry/SL/TP from the Rules panel — updates as controls change. */
  const selectedLevels = useMemo(() => {
    if (!hasScanned || !selectedMatch || windowCandles.length === 0) return null;
    const direction = strategyRdMatchDisplayDirection(selectedMatch);
    const preview = previewStrategyRdLevels({
      candles: windowCandles,
      barTime: selectedMatch.bar_time,
      direction,
      template: ruleTemplate,
      symbol: unitOpts.symbol,
      assetClass: unitOpts.assetClass,
    });
    if (preview?.valid) return preview;
    // Structural H/L stop/target — use BE path prices so caption + rails stay live.
    const pathLevels = strategyRdPathAppliedLevels(selectedMatch.path);
    if (!pathLevels) return null;
    return {
      entry: pathLevels.entry,
      stop: pathLevels.stop,
      target: pathLevels.target,
      direction: pathLevels.direction,
      risk: Math.abs(pathLevels.entry - pathLevels.stop),
      atr: null,
      valid: true,
      invalid_reason: null,
    };
  }, [hasScanned, ruleTemplate, selectedMatch, unitOpts, windowCandles]);

  /** Live rules preview — one Trade Review position tool per locked match. */
  const draftRuleTrades = useMemo(
    () =>
      hasScanned && windowCandles.length > 0
        ? strategyRdMatchesExecutionOverlays({
            matches,
            tradeIdFor: strategyRdMatchKey,
            candles: windowCandles,
            template: ruleTemplate,
            symbol: unitOpts.symbol,
            assetClass: unitOpts.assetClass,
          })
        : [],
    [hasScanned, matches, ruleTemplate, unitOpts, windowCandles]
  );

  /** Backtest fill per match key (Apply results are keyed by setup bar_time). */
  const applyFillByKey = useMemo(() => {
    const byTime = new Map<string, StrategyRdApplyResult>();
    for (const r of applyResults) {
      byTime.set(r.bar_time, r);
      if (r.pattern_id) byTime.set(`${r.pattern_id}:${r.bar_time}`, r);
    }
    const out = new Map<string, StrategyRdApplyResult>();
    for (const m of matches) {
      const r = byTime.get(m.bar_time) ?? byTime.get(strategyRdMatchKey(m));
      if (r?.fill) out.set(strategyRdMatchKey(m), r);
    }
    return out;
  }, [applyResults, matches]);

  /** Robustness `fragile_trades` → match keys (by side + entry instant). */
  const fragileKeys = useMemo(() => {
    const keys = new Set<string>();
    if (fragileTrades.length === 0) return keys;
    const wanted = new Set(
      fragileTrades.map((t) => `${t.side}|${Date.parse(t.entry_time)}`)
    );
    applyFillByKey.forEach((r, key) => {
      const f = r.fill;
      const t = Date.parse(f?.entry_time || f?.bar_time || "");
      if (wanted.has(`${f?.direction || r.direction}|${t}`)) keys.add(key);
    });
    return keys;
  }, [applyFillByKey, fragileTrades]);

  // After a backtest the chart shows the real fills: exit at `fill.exit_price`
  // (a gap fills at the open), stop / target as rounded to the tick.
  // Rule entries: resting orders as dashed lines; partial closes: one exit per piece.
  const scanRuleTrades = useMemo(() => {
    const matchByKey = new Map(matches.map((m) => [strategyRdMatchKey(m), m]));
    // Setups the backtest says it did not trade (daily limits, one at a time, the other side,
    // never filled): drawn as nothing — the chart shows the trades the recipe took, not previews.
    const notTraded = new Set<string>();
    if (applyResults.length > 0) {
      const byTime = new Map<string, StrategyRdApplyResult>();
      for (const r of applyResults) {
        byTime.set(r.bar_time, r);
        if (r.pattern_id) byTime.set(`${r.pattern_id}:${r.bar_time}`, r);
      }
      for (const m of matches) {
        const r = byTime.get(strategyRdMatchKey(m)) ?? byTime.get(m.bar_time);
        if (r && (!r.fill || r.fill.result === "skipped")) notTraded.add(strategyRdMatchKey(m));
      }
    }
    return draftRuleTrades.flatMap((t) => {
      const key = t.trade_id || "";
      if (notTraded.has(key)) return [];
      const match = matchByKey.get(key);
      const orderLines = strategyRdPathOrderLines(match?.path);
      const withOrders = orderLines.length > 0 ? { ...t, strategy_rd_order_lines: orderLines } : t;
      const applied = applyFillByKey.get(key);
      const fill = applied?.fill;
      if (!fill || fill.result === "skipped") return [withOrders];
      const closed = fill.result !== "open";
      const entryBarTime = match ? strategyRdMatchEntryBarTime(match) : null;
      const legs = closed
        ? strategyRdExitLegMarkers({
            candles: windowCandles,
            entryBarTime,
            exitTime: fill.exit_time,
            legs: applied?.forward_outcome.legs ?? [],
          })
        : [];
      // Break-even / trail: the stop drawn as it moved, so the management can be followed.
      const stopPath = strategyRdStopPathSteps({
        candles: windowCandles,
        entryBarTime: fill.entry_time ?? entryBarTime,
        exitTime: closed ? fill.exit_time : null,
        path: applied?.forward_outcome.stop_path,
      });
      const exitReason = closed
        ? strategyRdExitReason({ result: fill.result, note: applied?.forward_outcome.note, stopPath })
        : null;
      return [{
        ...withOrders,
        entry_price: fill.entry_price ?? t.entry_price,
        sl_price: fill.stop ?? t.sl_price,
        tp_price: fill.target ?? t.tp_price,
        exit_time: closed ? fill.exit_time ?? t.exit_time : t.exit_time,
        exit_price: closed ? fill.exit_price ?? null : null,
        entry_quantity: fill.qty ?? t.entry_quantity,
        net_pnl: fill.pnl ?? t.net_pnl,
        strategy_rd_fragile: fragileKeys.has(key) || undefined,
        ...(legs.length > 1 ? { strategy_rd_exit_legs: legs } : {}),
        ...(stopPath.length > 1 ? { strategy_rd_stop_path: stopPath } : {}),
        ...(exitReason ? { strategy_rd_exit_reason: exitReason } : {}),
      }];
    });
  }, [applyFillByKey, applyResults, draftRuleTrades, fragileKeys, matches, windowCandles]);

  /** Entry / stop / target per setup for the list's mini charts (the real fill once backtested). */
  const matchThumbLevels = useMemo(() => {
    const out: Record<string, { entry: number; stop: number; target: number; direction: "long" | "short" }> = {};
    for (const t of scanRuleTrades) {
      const entry = Number(t.entry_price);
      const stop = Number(t.sl_price);
      const target = Number(t.tp_price);
      if (!t.trade_id || !Number.isFinite(entry) || !Number.isFinite(stop) || !Number.isFinite(target)) continue;
      if (t.sl_price == null || t.tp_price == null) continue;
      out[t.trade_id] = { entry, stop, target, direction: t.direction === "short" ? "short" : "long" };
    }
    return out;
  }, [scanRuleTrades]);

  /** Each setup's backtest result for the setups list, keyed `pattern_id:bar_time` and `bar_time`. */
  const matchOutcomes = useMemo(() => {
    if (applyResults.length === 0) return null;
    const out: Record<string, { r: number | null; result: string | null }> = {};
    for (const row of applyResults) {
      const fill = row.fill;
      if (!fill) continue;
      const o = { r: fill.r ?? null, result: typeof fill.result === "string" ? fill.result : null };
      out[row.bar_time] = o;
      if (row.pattern_id) out[`${row.pattern_id}:${row.bar_time}`] = o;
    }
    return out;
  }, [applyResults]);

  const showIncompleteEntryBanner = strategyRdShouldShowIncompleteEntryBanner({
    entryGuidance,
    path: selectedMatch?.path,
  });
  const pathIncompleteReason = showIncompleteEntryBanner
    ? strategyRdIncompleteEntryMessage({
        entryGuidance,
        path: selectedMatch?.path,
      })
    : null;
  const entryTfUnavailableReason = useMemo(() => {
    const stats = hasScanned ? ohlcStats : previewOhlcStats;
    const fromStats = stats?.entry_tf_unavailable;
    if (typeof fromStats === "string" && fromStats.trim()) {
      return strategyRdPathInvalidReasonLabel(`entry_tf_unavailable:${fromStats.trim()}`);
    }
    if (pathIncompleteReason?.toLowerCase().includes("entry timeframe")) {
      return pathIncompleteReason;
    }
    return null;
  }, [hasScanned, ohlcStats, pathIncompleteReason, previewOhlcStats]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    void (async () => {
      setPresetsLoading(true);
      try {
        const next = await listStrategyRdCatalog(controller.signal);
        if (cancelled) return;
        // No default setup: the Lab stays empty until the user (or TES) builds a recipe.
        setCatalog(next);
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        if (isStrategyRdDisabledError(err)) {
          setDisabled(true);
          return;
        }
        presentMutationError(err, "Could not load Concepts catalog");
      } finally {
        if (!cancelled) setPresetsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    void (async () => {
      setInstrumentsLoading(true);
      try {
        const next = await listStrategyRdInstruments(controller.signal);
        if (cancelled) return;
        setInstruments(next.instruments);
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        if (isStrategyRdDisabledError(err)) {
          setDisabled(true);
          return;
        }
        presentMutationError(err, "Could not load instruments");
      } finally {
        if (!cancelled) setInstrumentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const onSetupChange = useCallback(
    (next: StrategyRdComposerSetupSlot) => {
      // A custom entry is written for one setup (its range, its edges). Another setup with the
      // rule left in place trades something nobody asked for: say so, with one click to reset.
      if (
        (next.id || "") !== (setupSlotRef.current.id || "") &&
        setupSlotRef.current.id &&
        ruleTemplateRef.current.entry.mode === "rule" &&
        ruleTemplateRef.current.entry.rule
      ) {
        toast.warning("Your custom entry rule was written for the previous setup", {
          description: "It is still in place. Check it fits the new setup in Backtest settings → Entry, or reset it.",
          duration: 12000,
          action: {
            label: "Use signal close",
            onClick: () => {
              setEntrySlot((prev) => ({ ...prev, mode: "signal_close" }));
              setRuleTemplate((prev) => ({ ...prev, entry: { ...prev.entry, mode: "signal_close", rule: null } }));
            },
          },
        });
      }
      setSetupSlot((prev) => strategyRdKeepSetupConditions(prev, next));
      setActivePackId(null);
      if (strategyRdSetupNeedsSession(next.id, catalog)) {
        // An opening-range setup needs one session: "All" (the page default) → London.
        setSessionIds((prev) =>
          prev.length > 0 && !prev.includes(STRATEGY_RD_SESSION_ALL)
            ? normalizeStrategyRdSessionIdsSelection(prev)
            : ["london"]
        );
      }
      // Concrete Setup trade side → Entry + Rules follow. High+Short / Low+Long → rejection.
      const setupSide = normalizeStrategyRdSlotSide(next.side);
      setEntrySlot((prev) => {
        const aligned = strategyRdAlignEntryToSetup(next, prev);
        const sideChanged =
          setupSide === "long" || setupSide === "short"
            ? normalizeStrategyRdSlotSide(prev.side) !== setupSide
            : false;
        const modeChanged = (aligned.mode || "") !== (prev.mode || "");
        if (!sideChanged && !modeChanged) return prev;
        return aligned;
      });
      if (setupSide === "long" || setupSide === "short") {
        const policy = strategyRdSidePolicyFromSlotSide(setupSide);
        setSidePolicy(policy);
        setRuleTemplate((prev) => {
          const alignedMode = strategyRdAlignEntryToSetup(next, {
            mode: prev.entry.mode,
            side: setupSide,
          }).mode;
          return {
            ...prev,
            entry: {
              ...prev.entry,
              side_policy: policy,
              mode: (alignedMode || prev.entry.mode) as typeof prev.entry.mode,
            },
          };
        });
      } else {
        setRuleTemplate((prev) => {
          const alignedMode = strategyRdAlignEntryToSetup(next, {
            mode: prev.entry.mode,
            side: next.side,
          }).mode;
          if ((alignedMode || "") === (prev.entry.mode || "")) return prev;
          return {
            ...prev,
            entry: {
              ...prev.entry,
              mode: (alignedMode || prev.entry.mode) as typeof prev.entry.mode,
            },
          };
        });
      }
    },
    [catalog]
  );

  const onPackSelect = useCallback(
    (pack: StrategyRdPack) => {
      const recipe = applyStrategyRdPackRecipe(pack, ruleTemplate);
      setActivePackId(pack.id);
      const setupConcept =
        catalog?.concepts.find((c) => c.id === recipe.setupId) || null;
      const setupBuilt = strategyRdBuildSlotFromSettings(
        setupConcept || { id: recipe.setupId, settings: null },
        {
          ...strategyRdDefaultSettingUiValues(setupConcept?.settings),
          side: strategyRdDefaultSetupSide(setupConcept, recipe.setupId),
        },
        { omitCandleType: true }
      );
      const builtSetup = strategyRdApplyBuiltSetupSlot(setupBuilt);
      const setupSlotNext: StrategyRdComposerSetupSlot = strategyRdRewriteSlotToPicker(
        catalog,
        builtSetup ?? {
          id: recipe.setupId,
          side: strategyRdDefaultSetupSide(setupConcept, recipe.setupId),
        }
      );
      setSetupSlot(setupSlotNext);
      setConfluenceSlots(
        recipe.confluences.map((id) => {
          const concept = catalog?.concepts.find((c) => c.id === id) || null;
          const built = strategyRdBuildSlotFromSettings(
            concept || { id, settings: null },
            {
              ...strategyRdDefaultSettingUiValues(concept?.settings),
              side: strategyRdDefaultConfluenceSide(concept, id),
              candle_type: candleType,
            }
          );
          const slot = strategyRdApplyBuiltConfluenceSlot(built);
          if (!slot.candle_type) slot.candle_type = candleType;
          return strategyRdRewriteSlotToPicker(catalog, slot);
        })
      );
      if (strategyRdHasClearOfOpposingSr(recipe.confluences)) {
        setSrScopes((prev) => (prev.length > 0 ? prev : [...STRATEGY_RD_SR_SCOPES]));
      }
      setSessionIds(recipe.sessionIds.length > 0 ? recipe.sessionIds : [STRATEGY_RD_SESSION_ALL]);
      const setupSide = normalizeStrategyRdSlotSide(setupSlotNext.side);
      setEntrySlot((prev) =>
        strategyRdAlignEntrySideToSetup(setupSlotNext, {
          ...prev,
          mode: recipe.template.entry.mode,
          candle_type: prev.candle_type || candleType,
        })
      );
      if (setupSide === "long" || setupSide === "short") {
        const policy = strategyRdSidePolicyFromSlotSide(setupSide);
        setSidePolicy(policy);
        setRuleTemplate({
          ...recipe.template,
          entry: { ...recipe.template.entry, side_policy: policy },
        });
      } else {
        setRuleTemplate(recipe.template);
      }
    },
    [candleType, catalog, ruleTemplate]
  );

  useEffect(() => {
    if (!catalog) return;
    setSetupSlot((prev) => strategyRdRewriteSlotToPicker(catalog, prev));
    setConfluenceSlots((prev) => {
      let changed = false;
      const next = prev.map((slot) => {
        const rewritten = strategyRdRewriteSlotToPicker(catalog, slot);
        if (rewritten !== slot) changed = true;
        return rewritten;
      });
      return changed ? next : prev;
    });
  }, [catalog]);


  const quoteOptions = useMemo(
    () => quoteVariantSelectOptions(quoteCandidates),
    [quoteCandidates]
  );
  const confluenceSkipped = useMemo(
    () => strategyRdConfluenceSkippedReasons(hasScanned ? ohlcStats : previewOhlcStats),
    [hasScanned, ohlcStats, previewOhlcStats]
  );
  const volumeAvailable = useMemo(
    () => strategyRdVolumeAvailableFromStats(hasScanned ? ohlcStats : previewOhlcStats),
    [hasScanned, ohlcStats, previewOhlcStats]
  );
  const confluenceSkipDisplay = useMemo(() => {
    const stats = hasScanned ? ohlcStats : previewOhlcStats;
    return [
      ...strategyRdConfluenceSkippedDisplay(stats),
      ...strategyRdConfluenceDroppedDisplay(stats),
      ...strategyRdConfluenceSoftPassNotes(stats),
    ];
  }, [hasScanned, ohlcStats, previewOhlcStats]);
  const previewDiagnostics = useMemo(
    () => (hasScanned ? [] : confluenceSkipDisplay),
    [confluenceSkipDisplay, hasScanned]
  );


  const resetIdentity = useCallback(() => {
    setResolvedSymbol("");
    setQuoteCandidates([]);
    setListing(null);
    scanAfterVariantRef.current = false;
    setPreviewMatches([]);
    setPreviewCandles([]);
    setPreviewCommitted(false);
    setPreviewOhlcStats(null);
    setPreviewMarkers([]);
    setPreviewSrRails([]);
    previewFingerprintRef.current = "";
  }, []);

  const onCandleTypeChange = useCallback((next: string) => {
    // The chart reports 1m / 1h as "m" / "h" (Trade Log tokens): normalise, and never fall
    // back to a default — an unknown value keeps the timeframe the trader is on.
    const api = toApiExecutionCandleType(next) ?? next;
    if (!(STRATEGY_RD_CANDLE_TYPES as readonly string[]).includes(api)) return;
    const tf = api as StrategyRdCandleType;
    setCandleType(tf);
    setLookbackDays((prev) => clampStrategyRdLookbackDays(prev, tf));
  }, []);

  const confirmIdentity = useCallback(
    async (
      nextSymbol: string,
      nextResolved: string | undefined,
      controller: AbortController,
      nextAssetClass?: string,
      brokerPins?: { market_data_broker?: string; md_source?: string }
    ): Promise<StrategyRdResolveResponse | null> => {
      if (!nextSymbol) return null;
      const listingResult = await resolveStrategyRd(
        {
          symbol: nextSymbol,
          asset_class: (nextAssetClass ?? assetClass).trim() || undefined,
          resolved_symbol: nextResolved,
          ...(brokerPins ?? instrumentBrokerPins),
        },
        controller.signal
      );
      if (controller.signal.aborted) return null;
      setListing(listingResult);
      setQuoteCandidates([]);
      if (listingResult.resolved_symbol) {
        setResolvedSymbol(listingResult.resolved_symbol);
      }
      if (listingResult.asset_class?.trim()) {
        setAssetClass(listingResult.asset_class.trim());
      }
      return listingResult;
    },
    [assetClass, instrumentBrokerPins]
  );

  const runScanWithIdentity = useCallback(
    async (identity: StrategyRdResolveResponse, controller: AbortController) => {
      if (!setupId) return;
      const alignedEntry = strategyRdAlignEntrySideToSetup(setupSlot, {
        mode: entrySlot.mode || ruleTemplate.entry.mode,
        side: entrySlot.side,
        candle_type: entrySlot.candle_type,
        max_wait: entrySlot.max_wait,
      });
      if (normalizeStrategyRdSlotSide(entrySlot.side) !== normalizeStrategyRdSlotSide(alignedEntry.side)) {
        setEntrySlot(alignedEntry);
      }
      const nested = compactNestedComposerFields({
        setup: setupSlot,
        confluences: confluenceSlots,
        entry: alignedEntry,
        setupCandleType: candleType,
      });
      const effectiveSidePolicy = strategyRdSidePolicyFromSlotSide(
        nested.entry?.side || setupSlot.side
      );
      if (sidePolicy !== effectiveSidePolicy) {
        setSidePolicy(effectiveSidePolicy);
        setRuleTemplate((prev) => ({
          ...prev,
          entry: strategyRdEntryWithMaxWait(
            { ...prev.entry, side_policy: effectiveSidePolicy },
            entrySlot
          ),
        }));
      }
      const request = {
        symbol: identity.symbol || symbol.trim().toUpperCase(),
        pattern_id: setupId,
        setup_id: setupId,
        setup: nested.setup || {
          id: setupId,
          side: normalizeStrategyRdSlotSide(setupSlot.side) || "both",
        },
        confluences: nested.confluences,
        ...(optionalMinWire ? { optional_min: optionalMinWire } : {}),
        entry: nested.entry ||
          strategyRdEntryWithMaxWait(
            {
              mode: entrySlot.mode || ruleTemplate.entry.mode,
              side: normalizeStrategyRdSlotSide(setupSlot.side) || "both",
              ...(entrySlot.candle_type?.trim()
                ? { candle_type: entrySlot.candle_type.trim() }
                : {}),
            },
            entrySlot
          ),
        sr_scopes: strategyRdSrScopesForRequest(srScopes, confluences),
        session_ids: normalizeStrategyRdSessionIdsSelection(sessionIds),
        candle_type: candleType,
        lookback_days: clampStrategyRdLookbackDays(lookbackDays, candleType),
        ...dateRangeBody,
        asset_class: identity.asset_class?.trim() || assetClass.trim() || undefined,
        resolved_symbol: identity.resolved_symbol || undefined,
        ...instrumentBrokerPins,
        entry_mode: nested.entry_mode || entrySlot.mode || ruleTemplate.entry.mode,
        side_policy: effectiveSidePolicy,
        chart_bars: STRATEGY_RD_CHART_BARS,
        template: withResolvedStrategyRdUnitSizes(
          {
            ...ruleTemplate,
            entry: strategyRdEntryWithMaxWait(
              {
                ...ruleTemplate.entry,
                mode: (entrySlot.mode || ruleTemplate.entry.mode) as typeof ruleTemplate.entry.mode,
                side_policy: effectiveSidePolicy,
              },
              entrySlot
            ),
          },
          {
            symbol: identity.resolved_symbol || symbol,
            assetClass: identity.asset_class?.trim() || assetClass,
          }
        ),
      };

      setBackfillStatus("Queued…");
      previewSkipRef.current = true;
      const result = await scanStrategyRd(request, controller.signal, (progress) => {
        setBackfillStatus(progress.message);
      });

      if (controller.signal.aborted) return;
      const nextMatches = result.matches.map((m) => ({
        ...m,
        direction: strategyRdMatchDisplayDirection(m),
      }));
      const nextCandles = strategyRdDetectWindowCandles(
        result.candles,
        scanCandlesRef.current.length > 0 ? scanCandlesRef.current : previewCandlesRef.current
      );
      // values[k] ↔ result.candles[k]: stamp the times before the window merge reshapes candles.
      setOhlcStats(strategyRdStatsWithSeriesBarTimes(result.ohlc_stats, result.candles));
      setMatches(nextMatches);
      setScanMarkers(resolveStrategyRdMatchMarkers(result.match_markers, nextMatches));
      setScanSrRails(result.sr_rails ?? []);
      setScanCandles(nextCandles);
      // The server keeps every scan (full matches with their setup data): the backtest always
      // runs from it. The page's copy of the matches is slimmed down and isn't enough.
      setScanJobId(result.scan_job_id ?? null);
      if (nextCandles.length > 0) setPreviewCandles(nextCandles);
      setLogicPath(result.logic_path ?? null);
      setEntryGuidance(result.entry_guidance ?? null);
      setConfluenceSlots((prev) =>
        strategyRdMergeComposerEchoInteractions(prev, result.composer)
      );
      setHasScanned(true);
      previewFingerprintRef.current = recipeFingerprint;
      setHypothesisId(null);
      setApplyResults([]);
      setApplyOverview(null);
      setApplyMeta(null);
      setReportStrategyHint(null);
      setConfirmName("");
      setSelectedKey((prev) => {
        if (prev && nextMatches.some((m) => strategyRdMatchKey(m) === prev)) return prev;
        const first = nextMatches[0];
        return first ? strategyRdMatchKey(first) : null;
      });
      window.setTimeout(() => {
        previewSkipRef.current = false;
      }, 400);
      const opened = pendingOpenRef.current;
      if (opened) {
        pendingOpenRef.current = null;
        restoreOpenedRef.current?.(opened);
      }
      if (applyAfterScanRef.current) {
        applyAfterScanRef.current = false;
        if (nextMatches.length > 0) {
          // Backtest once the new matches / candles are in state.
          setApplyToken((n) => n + 1);
        } else {
          toast.message("No trade-ready setups in this window", {
            description: "Nothing to backtest. Loosen the recipe, widen the lookback, or ask TES why.",
          });
        }
      }
    },
    [
      assetClass,
      candleType,
      confluenceSlots,
      confluences,
      entrySlot,
      instrumentBrokerPins,
      lookbackDays,
      optionalMinWire,
      recipeFingerprint,
      ruleTemplate,
      sessionIds,
      setupId,
      setupSlot,
      sidePolicy,
      srScopes,
      symbol,
    ]
  );

  const handleQuoteVariantRequired = useCallback((err: unknown) => {
    setQuoteCandidates(strategyRdQuoteVariantsFromError(err));
    setListing(null);
  }, []);

  const runScan = useCallback(
    async (resolvedOverride?: string) => {
      const nextSymbol = symbol.trim().toUpperCase();
      const nextResolved = strategyRdResolvedSymbolForRequest(
        quoteCandidates,
        resolvedOverride ?? resolvedSymbol
      );
      if (!nextSymbol || !setupId) return;

      scanAbortRef.current?.abort();
      const controller = new AbortController();
      scanAbortRef.current = controller;
      scanAfterVariantRef.current = true;

      setScanning(true);
      setFocusMatchToken(null);
      setBackfillStatus("Confirming instrument…");
      try {
        const identity = await confirmIdentity(nextSymbol, nextResolved, controller);
        if (!identity || controller.signal.aborted) return;
        if (identity.chart_available === false) {
          presentMutationError(
            new Error("Charts are not available for this listing."),
            "Scan failed"
          );
          return;
        }
        scanAfterVariantRef.current = false;
        await runScanWithIdentity(identity, controller);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (isStrategyRdDisabledError(err)) {
          setDisabled(true);
          return;
        }
        if (isStrategyRdQuoteVariantRequired(err)) {
          handleQuoteVariantRequired(err);
          return;
        }
        presentMutationError(err, "Scan failed");
      } finally {
        // A scan that didn't finish must not backtest later.
        if (applyAfterScanRef.current) applyAfterScanRef.current = false;
        if (scanAbortRef.current === controller) {
          setScanning(false);
          setBackfillStatus(null);
        }
      }
    },
    [
      confirmIdentity,
      handleQuoteVariantRequired,
      setupId,
      quoteCandidates,
      resolvedSymbol,
      runScanWithIdentity,
      symbol,
    ]
  );


  /** Timeframe / lookback change → drop cached warm OHLC so the next Preview re-warms. */
  useEffect(() => {
    setPreviewCandles([]);
    setPreviewMatches([]);
    setPreviewMarkers([]);
    setPreviewSrRails([]);
    setPreviewCommitted(false);
    setPreviewOhlcStats(null);
    setLogicPath(null);
    setEntryGuidance(null);
    previewFingerprintRef.current = "";
    setHasScanned(false);
    setMatches([]);
    setScanCandles([]);
    setScanJobId(null);
    setScanMarkers([]);
    setScanSrRails([]);
    setSelectedKey(null);
    setFocusMatchToken(null);
  }, [candleType, lookbackDays]);

  /** Recipe edits invalidate prior preview example (candles stay warm for the next Preview). */
  useEffect(() => {
    if (!previewFingerprintRef.current) return;
    if (recipeFingerprint === previewFingerprintRef.current) return;
    setPreviewMatches([]);
    setPreviewMarkers([]);
    setPreviewSrRails([]);
    setPreviewCommitted(false);
    setPreviewOhlcStats(null);
    setLogicPath(null);
    setEntryGuidance(null);
    setHasScanned(false);
    setMatches([]);
    setScanMarkers([]);
    setScanSrRails([]);
    setSelectedKey(null);
    setFocusMatchToken(null);
    setHypothesisId(null);
    setApplyResults([]);
    setApplyOverview(null);
    setApplyMeta(null);
  }, [recipeFingerprint]);

  /** A new TES chat starts from an empty Lab: no recipe, no setups on the chart, no results. */
  const resetLabForNewChat = useCallback(() => {
    setSetupSlot({ id: "" });
    setConfluenceSlots([]);
    setEntrySlot({ mode: "signal_close", side: "both" });
    setRuleTemplate(defaultStrategyRdRuleTemplate());
    setSessionIds([STRATEGY_RD_SESSION_ALL]);
    setActivePackId(null);
    previewFingerprintRef.current = "";
    livePreviewFpRef.current = "";
    setPreviewMatches([]);
    setPreviewMarkers([]);
    setPreviewSrRails([]);
    setPreviewCommitted(false);
    setPreviewOhlcStats(null);
    setLogicPath(null);
    setEntryGuidance(null);
    setHasScanned(false);
    setMatches([]);
    setScanCandles([]);
    setScanJobId(null);
    setScanMarkers([]);
    setScanSrRails([]);
    setSelectedKey(null);
    setFocusMatchToken(null);
    setHypothesisId(null);
    setApplyResults([]);
    setApplyOverview(null);
    setApplyMeta(null);
    // A ?backtest= link would reopen the old backtest on reload.
    const url = new URL(window.location.href);
    if (url.searchParams.has("backtest")) {
      url.searchParams.delete("backtest");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  }, []);

  const runPreviewConcepts = useCallback(async () => {
    if (hasScanned || scanning || !listing || !setupId.trim()) return;
    if (scanRulesBlockingRef.current) {
      toast.message(scanRulesBlockingRef.current);
      return;
    }
    // Preview always opens full-window; rail clicks own concept zoom.
    setFocusMatchToken(null);
    conceptsPreviewAbortRef.current?.abort();
    const controller = new AbortController();
    conceptsPreviewAbortRef.current = controller;
    const warmCandles = previewCandlesRef.current;
    const warmCandlesForRequest = strategyRdWarmCandlesForRequest(warmCandles);
    const fingerprint = recipeFingerprint;
    // A rule entry needs the whole template — never the entry-mode-only shortcut.
    const entryOnly =
      entrySlot.mode !== "rule" &&
      Boolean(fingerprint) &&
      fingerprint === previewFingerprintRef.current &&
      previewMatchesRef.current.length > 0 &&
      warmCandles.length > 0;

    setPreviewLoading(true);
    setBackfillStatus("Loading preview…");
    try {
      if (entryOnly) {
        const alignedEntry = strategyRdAlignEntrySideToSetup(setupSlot, {
          mode: entrySlot.mode || ruleTemplate.entry.mode,
          side: entrySlot.side,
          candle_type: entrySlot.candle_type,
        });
        const effectiveSidePolicy = strategyRdSidePolicyFromSlotSide(
          alignedEntry.side || setupSlot.side
        );
        if (normalizeStrategyRdSlotSide(entrySlot.side) !== normalizeStrategyRdSlotSide(alignedEntry.side)) {
          setEntrySlot(alignedEntry);
        }
        if (sidePolicy !== effectiveSidePolicy) {
          setSidePolicy(effectiveSidePolicy);
          setRuleTemplate((prev) => ({
            ...prev,
            entry: { ...prev.entry, side_policy: effectiveSidePolicy },
          }));
        }
        const previewed = await previewStrategyRdPath(
          {
            matches: previewMatchesRef.current,
            candles: warmCandles,
            entry_mode: ruleTemplate.entry.mode,
            side_policy: effectiveSidePolicy,
          },
          controller.signal
        );
        if (controller.signal.aborted) return;
        const nextMatches = takeStrategyRdPreviewExamples(
          mergeStrategyRdPreviewPaths(previewMatchesRef.current, previewed.matches)
        ).map((m) => ({
          ...m,
          direction: strategyRdMatchDisplayDirection(m),
        }));
        setPreviewMatches(nextMatches);
        setPreviewCommitted(true);
        if (nextMatches.length > 0) setPreviewEmptyState(null);
        return;
      }

      const alignedEntry = strategyRdAlignEntrySideToSetup(setupSlot, {
        mode: entrySlot.mode || ruleTemplate.entry.mode,
        side: entrySlot.side,
        candle_type: entrySlot.candle_type,
        max_wait: entrySlot.max_wait,
      });
      if (normalizeStrategyRdSlotSide(entrySlot.side) !== normalizeStrategyRdSlotSide(alignedEntry.side)) {
        setEntrySlot(alignedEntry);
      }
      const nested = compactNestedComposerFields({
        setup: setupSlot,
        confluences: confluenceSlots,
        entry: alignedEntry,
        setupCandleType: candleType,
      });
      const effectiveSidePolicy = strategyRdSidePolicyFromSlotSide(
        nested.entry?.side || setupSlot.side
      );
      if (sidePolicy !== effectiveSidePolicy) {
        setSidePolicy(effectiveSidePolicy);
        setRuleTemplate((prev) => ({
          ...prev,
          entry: strategyRdEntryWithMaxWait(
            { ...prev.entry, side_policy: effectiveSidePolicy },
            entrySlot
          ),
        }));
      }
      const result = await previewStrategyRdConcepts(
        {
          symbol: listing.symbol || symbol.trim().toUpperCase(),
          setup_id: setupId,
          pattern_id: setupId,
          setup: nested.setup || {
            id: setupId,
            side: normalizeStrategyRdSlotSide(setupSlot.side) || "both",
          },
          confluences: nested.confluences,
          ...(optionalMinWire ? { optional_min: optionalMinWire } : {}),
          entry: nested.entry ||
            strategyRdEntryWithMaxWait(
              {
                mode: entrySlot.mode || ruleTemplate.entry.mode,
                side: normalizeStrategyRdSlotSide(setupSlot.side) || "both",
                ...(entrySlot.candle_type?.trim()
                  ? { candle_type: entrySlot.candle_type.trim() }
                  : {}),
              },
              entrySlot
            ),
          sr_scopes: strategyRdSrScopesForRequest(srScopes, confluences),
          session_ids: normalizeStrategyRdSessionIdsSelection(sessionIds),
          candle_type: candleType,
          lookback_days: clampStrategyRdLookbackDays(lookbackDays, candleType),
          ...dateRangeBody,
          asset_class: listing.asset_class?.trim() || assetClass.trim() || undefined,
          resolved_symbol: listing.resolved_symbol || resolvedSymbol || undefined,
          ...instrumentBrokerPins,
          entry_mode: nested.entry_mode || entrySlot.mode || ruleTemplate.entry.mode,
          side_policy: effectiveSidePolicy,
          template: {
            ...ruleTemplate,
            entry: strategyRdEntryWithMaxWait(
              {
                ...ruleTemplate.entry,
                mode: (entrySlot.mode || ruleTemplate.entry.mode) as typeof ruleTemplate.entry.mode,
                side_policy: effectiveSidePolicy,
              },
              entrySlot
            ),
          },
          candles: warmCandlesForRequest,
        },
        controller.signal,
        (progress) => {
          setBackfillStatus(progress.message);
        }
      );
      if (controller.signal.aborted) return;
      const nextMatches = takeStrategyRdPreviewExamples(result.matches).map((m) => ({
        ...m,
        direction: strategyRdMatchDisplayDirection(m),
      }));
      if (result.candles && result.candles.length > 0) {
        setPreviewCandles(result.candles);
      }
      setPreviewMatches(nextMatches);
      setPreviewMarkers(resolveStrategyRdMatchMarkers(result.match_markers, nextMatches));
      setPreviewSrRails(result.sr_rails ?? []);
      setLogicPath(result.logic_path ?? null);
      setEntryGuidance(result.entry_guidance ?? null);
      setConfluenceSlots((prev) =>
        strategyRdMergeComposerEchoInteractions(prev, result.composer)
      );
      previewFingerprintRef.current = fingerprint;
      setPreviewCommitted(true);
      setPreviewOhlcStats(
        strategyRdStatsWithSeriesBarTimes(result.ohlc_stats ?? null, result.candles)
      );
      setPreviewEmptyState(
        nextMatches.length === 0 ? result.empty_state ?? null : null
      );
      setSelectedKey((prev) => {
        if (prev && nextMatches.some((m) => strategyRdMatchKey(m) === prev)) return prev;
        const first = nextMatches[0];
        return first ? strategyRdMatchKey(first) : null;
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (isStrategyRdDisabledError(err)) {
        setDisabled(true);
        return;
      }
      if (isStrategyRdQuoteVariantRequired(err)) {
        handleQuoteVariantRequired(err);
        return;
      }
      presentMutationError(err, "Could not preview concepts");
    } finally {
      if (!controller.signal.aborted) {
        setPreviewLoading(false);
        setBackfillStatus(null);
      }
    }
  }, [
    assetClass,
    candleType,
    confluenceSlots,
    confluences,
    entrySlot,
    handleQuoteVariantRequired,
    hasScanned,
    instrumentBrokerPins,
    listing,
    lookbackDays,
    optionalMinWire,
    recipeFingerprint,
    resolvedSymbol,
    ruleTemplate,
    scanning,
    sessionIds,
    setupId,
    setupSlot,
    sidePolicy,
    srScopes,
    symbol,
  ]);


  const applyCoachComposer = useCallback(
    (next: StrategyRdCoachLabApply) => {
      if (
        next.candleType &&
        STRATEGY_RD_CANDLE_TYPES.includes(next.candleType as StrategyRdCandleType)
      ) {
        setCandleType(next.candleType as StrategyRdCandleType);
      }
      if (typeof next.lookbackDays === "number" && next.lookbackDays > 0) {
        setLookbackDays(
          clampStrategyRdLookbackDays(
            next.lookbackDays,
            (next.candleType as StrategyRdCandleType) || candleType
          )
        );
      }
      const nextSetup = strategyRdRewriteSlotToPicker(catalog, next.setup);
      const nextConfluences = next.confluences.map((slot) =>
        strategyRdRewriteSlotToPicker(catalog, {
          ...slot,
          candle_type: slot.candle_type || next.candleType || candleType,
        })
      );
      const aligned = strategyRdAlignEntrySideToSetup(nextSetup, {
        mode: parseStrategyRdEntryMode(next.entry.mode || "break_close"),
        side: next.entry.side,
        candle_type: next.entry.candle_type || next.candleType || candleType,
      });
      const nextSessions = next.sessionIds?.length
        ? normalizeStrategyRdSessionIdsSelection(next.sessionIds)
        : sessionIds;
      const touchKeys = strategyRdComposerTouchKeys(
        {
          setup: setupSlot,
          confluences: confluenceSlots,
          entry: entrySlot,
          sessionIds,
          candleType,
        },
        {
          setup: nextSetup,
          confluences: nextConfluences,
          entry: aligned,
          sessionIds: nextSessions,
          candleType: next.candleType || candleType,
        }
      );
      if (touchKeys.length > 0) {
        setComposerFlash((prev) => ({ token: prev.token + 1, keys: touchKeys }));
      }
      setSetupSlot(nextSetup);
      setConfluenceSlots(nextConfluences);
      setEntrySlot(aligned);
      const policy = strategyRdSidePolicyFromSlotSide(
        aligned.side || nextSetup.side
      );
      setSidePolicy(policy);
      // Composer owns catalog entry modes only. Never write bare {mode:"rule"} into the
      // template — custom Entry rule bodies come from turn.template / template_patch.
      setRuleTemplate((prev) => ({
        ...prev,
        entry: strategyRdEntryWithMaxWait(
          strategyRdTemplateEntryFromComposerMode(prev.entry, aligned.mode, policy),
          aligned
        ),
      }));
      if (next.sessionIds?.length) {
        setSessionIds(normalizeStrategyRdSessionIdsSelection(next.sessionIds));
      }
      if (next.packId) setActivePackId(next.packId);
      if (next.optionalMin != null) setOptionalMin(next.optionalMin);
      setPreviewCommitted(false);
      previewFingerprintRef.current = "";
    },
    [candleType, catalog, confluenceSlots, entrySlot, sessionIds, setupSlot]
  );

  const coachOutcomeSummary = useMemo((): StrategyRdOutcomeSummary | null => {
    if (!applyOverview) return null;
    return {
      target: applyOverview.target_count ?? 0,
      stop: applyOverview.stop_count ?? 0,
      open: applyOverview.open_count ?? 0,
      skipped: applyOverview.skipped_count ?? 0,
    };
  }, [applyOverview]);

  // --- §10.3 all trades, Open in Lab ---------------------------------------------------
  /** Apply / last_apply carry the first 100 trades; the rest come from `/trades`. */
  const loadAllTrades = useCallback(async (hid: string, shown: number) => {
    if (shown < 100) return;
    try {
      const all = await listAllStrategyRdHypothesisTrades(hid);
      if (hypothesisIdRef.current !== hid) return;
      if (all.trades.length > shown) setApplyResults(all.trades);
      setTradesNote(all.complete ? null : `Showing the first ${Math.max(shown, all.trades.length)} trades.`);
    } catch {
      setTradesNote(`Showing the first ${shown} trades.`);
    }
  }, []);
  loadAllTradesRef.current = loadAllTrades;

  /** After the opened backtest's scan: its saved results on the fresh matches. */
  restoreOpenedRef.current = (h: StrategyRdHypothesis) => {
    setHypothesisId(h.id);
    setAppliedTemplateKey(JSON.stringify(h.template));
    hypothesisIdRef.current = h.id;
    const last = h.last_apply;
    if (last?.results?.length) {
      setApplyResults(last.results);
      setApplyOverview(last.overview ?? null);
      setApplyMeta({
        applied_at: last.applied_at ?? null,
        sizing: last.sizing ?? null,
        warnings: last.overview?.warnings ?? null,
      });
      void loadAllTrades(h.id, last.results.length);
    }
    setOpenedRobustness(
      h.last_robustness && h.last_robustness.applied_at === last?.applied_at ? h.last_robustness : null
    );
    // The scan before this cleared it: an opened backtest that's saved shows "Saved · Open".
    setReportStrategyHint(h.report_strategy_hint ?? null);
    setVersionsRefresh((n) => n + 1);
  };

  // The coach is created further down; backtest opens reach its chat through this.
  const coachRef = useRef<ReturnType<typeof useStrategyRdCoach> | null>(null);
  /** Open a saved backtest (TES card, versions list): market, recipe, rules, then its results.
   *  Resolves to its name once opened, or null when it couldn't be. */
  const openHypothesis = useCallback(
    async (id: string): Promise<string | null> => {
      setResultsTab("overview");
      try {
        const h = await getStrategyRdHypothesis(id);
        const ctx = h.scan_context;
        const slots = parseCoachComposerSlots(
          ctx.composer ?? {
            setup: ctx.setup ?? (ctx.setup_id ? { id: ctx.setup_id } : undefined),
            confluences: ctx.confluences ?? [],
            entry: ctx.entry ?? undefined,
          }
        );
        marketWindowTouchedRef.current = true;
        const sym = ctx.symbol.trim().toUpperCase();
        const inst = instruments.find((row) => row.display_symbol.trim().toUpperCase() === sym);
        if (inst) setSelectedInstrumentId(inst.instrument_id);
        setSymbol(sym);
        setAssetClass(ctx.asset_class?.trim() || inst?.asset_class?.trim() || "");
        setResolvedSymbol(ctx.resolved_symbol?.trim() || "");
        if (ctx.candle_type && STRATEGY_RD_CANDLE_TYPES.includes(ctx.candle_type as StrategyRdCandleType)) {
          setCandleType(ctx.candle_type as StrategyRdCandleType);
        }
        if (ctx.lookback_days) setLookbackDays(ctx.lookback_days);
        setDateRange(ctx.start_date && ctx.end_date ? { start: ctx.start_date, end: ctx.end_date } : null);
        // The opened backtest's own name heads the results ("ORB Retest …", not the setup id).
        setOpenedBacktest({ id, name: typeof h.name === "string" ? h.name.trim() : "" });
        setOpenedMarkets(h.last_markets ?? null);
        if (ctx.session_ids?.length) setSessionIds(normalizeStrategyRdSessionIdsSelection(ctx.session_ids));
        if (ctx.sr_scopes?.length) setSrScopes(ctx.sr_scopes);
        if (slots) {
          setSetupSlot(slots.setup);
          setConfluenceSlots(slots.confluences);
          // A backtest whose rules carry a custom entry trades by that rule, whatever preset its
          // recipe header was saved with (TES runs stored "signal close" there).
          const customEntry = h.template?.entry?.mode === "rule" && h.template.entry.rule != null;
          // …and the other way round: a header left on "rule" after the rules went back to a preset.
          const savedMode = h.template?.entry?.mode;
          const openedEntry = customEntry
            ? { ...slots.entry, mode: "rule" as const }
            : slots.entry.mode === "rule"
              ? { ...slots.entry, mode: savedMode && savedMode !== "rule" ? savedMode : ("signal_close" as const) }
              : slots.entry;
          // The saved wait window too (a TES run stores it in the rules): without it the page
          // would re-scan with the default wait and find fewer entries than the backtest.
          const savedWait = h.template?.entry?.max_wait ?? null;
          setEntrySlot(openedEntry.max_wait == null && savedWait ? { ...openedEntry, max_wait: savedWait } : openedEntry);
          if (slots.entry.mode && slots.entry.mode !== "rule") presetEntryModeRef.current = slots.entry.mode;
        }
        setOptionalMin(ctx.optional_min ?? slots?.optionalMin ?? 1);
        setRuleTemplate(h.template);
        if (h.last_apply?.sizing) setSizing(h.last_apply.sizing);
        if (h.last_apply?.starting_equity) setStartingEquity(h.last_apply.starting_equity);
        setRuleFieldErrors(null);
        setTradesNote(null);
        setInstrumentStepConfirmed(true);
        pendingOpenRef.current = h;
        openedInfoRef.current = {
          id: h.id,
          version: h.version ?? null,
          saved: Boolean(h.report_strategy_hint?.report_strategy_id),
        };
        // Scan once the state above has rendered (runScan reads it).
        setOpenScanToken((n) => n + 1);
        return typeof h.name === "string" ? h.name.trim() : "";
      } catch (err) {
        if (isStrategyRdDisabledError(err)) {
          setDisabled(true);
          return null;
        }
        presentMutationError(err, "Could not open the backtest");
        return null;
      }
    },
    [instruments]
  );
  /** From history (library, versions, links): the backtest and the chat that ran it, so the
   *  user can pick the conversation up. TES's own cards don't switch chat — they're in it. */
  const openBacktestWithChat = useCallback(
    (id: string, opts?: { stayIfUnlinked?: boolean }) =>
      openHypothesis(id).then((name) => {
        const info = openedInfoRef.current?.id === id ? openedInfoRef.current : null;
        if (name !== null) {
          void coachRef.current?.openChatForBacktest(id, name, {
            ...opts,
            version: info?.version ?? null,
            saved: info?.saved ?? false,
          });
        }
        return name;
      }),
    [openHypothesis]
  );
  /** ?backtest=<id> opens that backtest (links from TES, shared links). */
  const openedFromUrlRef = useRef(false);
  useEffect(() => {
    if (openedFromUrlRef.current) return;
    const id = new URLSearchParams(window.location.search).get("backtest");
    if (!id) return;
    // After the market list: opening matches the backtest's symbol to a listed market, and
    // without one (a reload opens straight away) the scan never starts.
    if (instrumentsLoading || instruments.length === 0) return;
    openedFromUrlRef.current = true;
    void openBacktestWithChat(id);
  }, [instruments.length, instrumentsLoading, openBacktestWithChat]);
  /** The open backtest goes in the address, so a reload or a copied link comes back to it. */
  useEffect(() => {
    if (!hypothesisId) return;
    openedFromUrlRef.current = true;
    const url = new URL(window.location.href);
    if (url.searchParams.get("backtest") === hypothesisId) return;
    url.searchParams.set("backtest", hypothesisId);
    window.history.replaceState(window.history.state, "", url.toString());
  }, [hypothesisId]);
  useEffect(() => {
    if (!openScanToken) return;
    void runScan();
    // runScan is the one from the render that applied the opened recipe.
  }, [openScanToken]);

  // --- §10 TES proposals -----------------------------------------------------------------
  const SIZING_FIELD_FOCUS: Record<string, string> = {
    leverage: "sizing.leverage",
    position_mode: "sizing.position_mode",
    commission_pct: "sizing.commission_pct",
    slippage_ticks: "sizing.slippage_ticks",
    limits: "sizing.limits.max_trades_per_day",
    value: "sizing.value",
  };
  /**
   * Confirm card: prefer full turn.template / turn.sizing replace; else deep-merge
   * template_patch / sizing_patch. Never set template.entry.mode from composer alone.
   */
  const applyCoachProposal = useCallback(
    (proposal: {
      template?: Record<string, unknown> | null;
      template_patch?: Record<string, unknown> | null;
      sizing?: Record<string, unknown> | null;
      sizing_patch?: Record<string, unknown> | null;
    }) => {
      const fullTpl = proposal.template ?? null;
      const tplPatch = proposal.template_patch ?? null;
      if (fullTpl || tplPatch) {
        const resolved = fullTpl ? parseStrategyRdRuleTemplate(fullTpl) : null;
        const entryPatch =
          !fullTpl && tplPatch?.entry && typeof tplPatch.entry === "object"
            ? (tplPatch.entry as Record<string, unknown>)
            : null;
        const nextMode = resolved?.entry.mode ?? (typeof entryPatch?.mode === "string" ? entryPatch.mode : null);
        const toRule = nextMode === "rule";
        const fromRule =
          !fullTpl &&
          entryPatch != null &&
          entryPatch.rule === null &&
          entryPatch.mode !== "rule";
        setEntrySlot((prev) => {
          if (toRule && prev.mode !== "rule") {
            presetEntryModeRef.current = prev.mode || "signal_close";
            return { ...prev, mode: "rule" };
          }
          if ((fromRule || (resolved && resolved.entry.mode !== "rule")) && prev.mode === "rule") {
            const mode =
              (typeof entryPatch?.mode === "string" ? entryPatch.mode : null) ||
              resolved?.entry.mode ||
              presetEntryModeRef.current;
            return { ...prev, mode: mode || "signal_close" };
          }
          return prev;
        });
        setRuleTemplate((prev) => {
          const next = strategyRdConfirmCoachTemplate(
            prev,
            { template: fullTpl, template_patch: tplPatch },
            parseStrategyRdRuleTemplate
          );
          if (!fullTpl && fromRule && next.entry.mode === "rule") {
            return {
              ...next,
              entry: {
                ...next.entry,
                mode: (presetEntryModeRef.current || "signal_close") as StrategyRdEntryMode,
              },
            };
          }
          return next;
        });
      }
      const fullSz = proposal.sizing ?? null;
      const szPatch = proposal.sizing_patch ?? null;
      if (fullSz || szPatch) {
        if (fullSz) {
          const parsed = parseStrategyRdSizing(fullSz);
          if (parsed) setSizing(parsed);
        } else if (szPatch) {
          setSizing((prev) => strategyRdDeepMergePatch(prev, szPatch));
        }
        const focusSource = fullSz || szPatch || {};
        const field = Object.keys(focusSource)
          .map((k) => SIZING_FIELD_FOCUS[k])
          .find(Boolean);
        if (field) setSettingFocus({ field, token: Date.now() });
      }
      setRuleFieldErrors(null);
      // Show exactly what changed: the settings drawer, its editors filled in.
      if (fullTpl || tplPatch || fullSz || szPatch) setSettingsOpen(true);
    },
    // SIZING_FIELD_FOCUS is constant.
    []
  );

  /** Every TES message carries the whole Lab, unsaved edits included (§10). */
  const getCoachLabState = useCallback(
    () => ({
      hypothesisId: hypothesisIdRef.current,
      template: ruleTemplateRef.current as unknown as Record<string, unknown>,
      sizing: {
        ...(strategyRdSizingForWire(sizingRef.current) as unknown as Record<string, unknown>),
      },
    }),
    []
  );

  const coach = useStrategyRdCoach({
    enabled: !disabled,
    // Chat-first Lab: TES is always open beside the chart.
    autoOpen: true,
    dock: "left",
    symbol: listing?.resolved_symbol || symbol,
    assetClass,
    candleType,
    sessionIds,
    lookbackDays,
    setup: setupSlot,
    confluences: confluenceSlots,
    entry: entrySlot,
    ohlcStats: hasScanned ? ohlcStats : previewOhlcStats,
    logicPath,
    outcomeSummary: coachOutcomeSummary,
    previewCommitted,
    hasScanned,
    matchCount: hasScanned ? matches.length : previewMatches.length,
    onApplyComposer: applyCoachComposer,
    getLabState: getCoachLabState,
    onApplyProposal: applyCoachProposal,
    onLabActionFinished: (action) => {
      setVersionsRefresh((n) => n + 1);
      // Its id only exists once the run is done: link it now, so reopening it brings this chat.
      if (action.status === "ready" && action.hypothesis_id) {
        void coachRef.current?.linkBacktestToChat(action.hypothesis_id);
      }
      // A TES backtest lands in the Lab, under the chart — unless the user is mid-run.
      if (
        action.status === "ready" &&
        action.hypothesis_id &&
        (action.kind === "backtest" || action.kind === "sweep" || action.kind === "search") &&
        !labBusyRef.current
      ) {
        void openHypothesis(action.hypothesis_id);
      }
    },
    onAfterComposerApplied: () => {
      setCoachKickPreview((n) => n + 1);
    },
    onLabRunPreview: () => {
      setCoachKickPreview((n) => n + 1);
    },
    onLabFocusComposer: (section) => {
      const keys =
        section === "entry"
          ? ["entry"]
          : section === "confluences"
            ? ["confluence:0"]
            : ["setup", "sessions"];
      // The composer lives in the Recipe drawer: open it on the section TES points at.
      setRecipeOpen(true);
      setComposerFlash((prev) => ({ token: prev.token + 1, keys }));
    },
    onLabOpenRecipe: () => {
      setSettingsOpen(false);
      setRecipeOpen(true);
    },
    onLabOpenBacktestSettings: () => {
      setRecipeOpen(false);
      setSettingsOpen(true);
    },
    // The Versions tab shows the run next to the one before it.
    onLabCompareVersions: () => setResultsTab("versions"),
    // Opens the save dialog once the backtest's results are on the page.
    onLabSaveStrategy: () => setPendingSaveId(hypothesisId),
  });
  /**
   * A drawer (settings, setups, Pine, chart) needs room: when TES, the drawer and the chart
   * can't all fit, TES folds to its strip while the drawer is open and comes back after.
   */
  const drawerOpen = settingsPanel != null || labPanel != null;
  const coachFoldedForDrawerRef = useRef(false);
  useEffect(() => {
    if (drawerOpen) {
      const labWidth = coachHostRef.current?.clientWidth ?? window.innerWidth;
      const drawerWidth = Math.max(420, Math.round(window.innerWidth * 0.38));
      const chartRoom = labWidth - coach.panelWidth - drawerWidth - LAB_TASKBAR_WIDTH;
      if (coach.panelOpen && chartRoom < LAB_CHART_MIN_COMFORT) {
        coachFoldedForDrawerRef.current = true;
        coach.foldPanel();
      }
    } else if (coachFoldedForDrawerRef.current) {
      coachFoldedForDrawerRef.current = false;
      coach.openPanel();
    }
    // Only on the drawer opening or closing; TES moved by hand stays where it is put.
  }, [drawerOpen]);
  coachRef.current = coach;
  /** Results "Ask TES" actions: open the coach and send the question. */
  const askTes = useCallback(
    (question: string) => {
      coach.openPanel();
      void coach.sendTurn(question);
    },
    [coach]
  );

  /** The Lab opens on a market; the asset is chosen on the chart toolbar. */
  const labResearchOpen = Boolean(catalog);

  /** Pick an asset on the chart: confirm its listing at once (no separate market step). */
  const instrumentAbortRef = useRef<AbortController | null>(null);
  const pickInstrument = useCallback(
    async (instrumentId: string) => {
      const picked = instruments.find((row) => row.instrument_id === instrumentId);
      if (!picked) return;
      const nextSymbol = picked.display_symbol.trim().toUpperCase();
      const nextAsset = picked.asset_class?.trim() || "";
      instrumentAbortRef.current?.abort();
      scanAbortRef.current?.abort();
      const controller = new AbortController();
      instrumentAbortRef.current = controller;
      setSelectedInstrumentId(picked.instrument_id);
      setSymbol(nextSymbol);
      setAssetClass(nextAsset);
      resetIdentity();
      setHasScanned(false);
      setMatches([]);
      setScanCandles([]);
      setScanJobId(null);
      setScanMarkers([]);
      setScanSrRails([]);
      setOhlcStats(null);
      setSelectedKey(null);
      setHypothesisId(null);
      setApplyResults([]);
      setApplyOverview(null);
      setApplyMeta(null);
      try {
        const identity = await confirmIdentity(
          nextSymbol,
          undefined,
          controller,
          nextAsset,
          strategyRdInstrumentBrokerPins(picked)
        );
        if (controller.signal.aborted || !identity) return;
        if (identity.chart_available === false) {
          presentMutationError(
            new Error("Charts are not available for this listing."),
            "Could not open this asset"
          );
          return;
        }
        setInstrumentStepConfirmed(true);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (isStrategyRdQuoteVariantRequired(err)) {
          handleQuoteVariantRequired(err);
          return;
        }
        presentMutationError(err, "Could not open this asset");
      }
    },
    [confirmIdentity, handleQuoteVariantRequired, instruments, resetIdentity]
  );

  // First visit: open a liquid market so the chart is never empty.
  useEffect(() => {
    if (selectedInstrumentId || instrumentsLoading || instruments.length === 0) return;
    if (marketWindowTouchedRef.current) return;
    const bySymbol = (sym: string) =>
      instruments.find((row) => row.display_symbol.trim().toUpperCase() === sym);
    const picked =
      bySymbol("XAUUSD") || bySymbol("XAU/USD") || bySymbol("EURUSD") || bySymbol("BTCUSDT") || instruments[0];
    marketWindowTouchedRef.current = true;
    // Gold on 15m — the Lab's default market.
    onCandleTypeChange("15m");
    void pickInstrument(picked.instrument_id);
  }, [instruments, instrumentsLoading, onCandleTypeChange, pickInstrument, selectedInstrumentId]);

  /** The chart's own symbol search lists the Lab's instruments; its bars come from the Lab. */
  const chartSymbolProvider = useMemo(
    () => ({
      symbols: instruments.map((row) => ({
        ticker: row.display_symbol.trim().toUpperCase(),
        // Never the data vendor: traders see the market's name only.
        description: row.display_name?.trim() || undefined,
        type: row.asset_class?.trim() || undefined,
      })),
      getBars: async (
        ticker: string,
        tf: string,
        range: { from?: number; to?: number; limit?: number }
      ) => {
        const end = range.to ? new Date(range.to) : new Date();
        const start = range.from
          ? new Date(range.from)
          : new Date(end.getTime() - clampStrategyRdLookbackDays(lookbackDays, tf as StrategyRdCandleType) * 86_400_000);
        const inst = instruments.find((row) => row.display_symbol.trim().toUpperCase() === ticker.toUpperCase());
        const res = await fetchStrategyRdCandles({
          symbol: ticker,
          candle_type: tf,
          start_date: start.toISOString(),
          end_date: end.toISOString(),
          asset_class: inst?.asset_class ?? null,
        });
        return res.candles.map((c) => ({
          time: Date.parse(c.timestamp),
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
          volume: c.volume ?? undefined,
        }));
      },
    }),
    [instruments, lookbackDays]
  );
  const onChartInstrumentChange = useCallback(
    (raw: string) => {
      const sym = raw.trim().toUpperCase();
      const inst = instruments.find((row) => row.display_symbol.trim().toUpperCase() === sym);
      if (!inst) {
        toast.message(`${sym} isn't available in the Lab`, {
          description: "Pick one of the listed assets.",
        });
        return;
      }
      if (inst.instrument_id === selectedInstrumentId) return;
      marketWindowTouchedRef.current = true;
      void pickInstrument(inst.instrument_id);
    },
    [instruments, pickInstrument, selectedInstrumentId]
  );

  // The market's candles as soon as there's a listing — the chart shows before any recipe,
  // and Preview reuses them as its warm candles. Lab candles: central lane, no account.
  // One load per market window (symbol · timeframe · lookback): a new window aborts the old
  // load; drawing partial candles or a Preview starting leaves an in-flight load alone.
  const [marketCandlesStatus, setMarketCandlesStatus] = useState<string | null>(null);
  const chartFromLabel = useMemo(() => {
    const t = windowCandles[0]?.timestamp;
    const d = t ? new Date(t) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { month: "short", year: "numeric" }) : null;
  }, [windowCandles]);
  const marketLoadRef = useRef<{ key: string; controller: AbortController } | null>(null);
  const marketKey = listing
    ? `${listing.resolved_symbol || listing.symbol}|${candleType}|${lookbackDays}`
    : "";
  // Candles the chart holds from a market load (not a Preview or a backtest): a new window
  // or timeframe reloads them, so the window picker always does something visible.
  const marketCandlesRef = useRef<{ key: string; bars: StrategyRdOhlcBar[] } | null>(null);
  const holdsStaleMarketCandles =
    previewCandles.length > 0 &&
    marketCandlesRef.current != null &&
    marketCandlesRef.current.bars === previewCandles &&
    marketCandlesRef.current.key !== marketKey;
  const needMarketCandles =
    Boolean(listing) &&
    (previewCandles.length === 0 || holdsStaleMarketCandles) &&
    scanCandles.length === 0 &&
    !previewLoading &&
    !scanning;
  useEffect(() => {
    const current = marketLoadRef.current;
    if (current && current.key !== marketKey) {
      current.controller.abort();
      marketLoadRef.current = null;
    }
    if (!listing || !marketKey || !needMarketCandles || marketLoadRef.current) return;
    const controller = new AbortController();
    marketLoadRef.current = { key: marketKey, controller };
    // The previous window's market candles stay on screen until the new ones arrive.
    const stale = marketCandlesRef.current?.key !== marketKey ? marketCandlesRef.current?.bars ?? null : null;
    const end = new Date();
    // The most recent bars only: long windows are scanned and backtested server-side.
    const start = new Date(strategyRdChartStartMs(end.getTime(), lookbackDays, candleType));
    setMarketCandlesStatus("Loading chart…");
    let partialShown = false;
    fetchStrategyRdCandles(
      {
        symbol: listing.symbol || listing.resolved_symbol,
        candle_type: candleType,
        start_date: start.toISOString(),
        end_date: end.toISOString(),
        asset_class: listing.asset_class ?? null,
        resolved_symbol: listing.resolved_symbol ?? null,
        market_data_broker: listing.market_data_broker ?? null,
      },
      {
        signal: controller.signal,
        // A cold window: draw what's there while the rest loads.
        onPartial: (bars, progress) => {
          if (bars.length > 0 && !partialShown) {
            partialShown = true;
            setPreviewCandles((prev) => {
              if (prev.length > 0 && prev !== stale) return prev;
              marketCandlesRef.current = { key: marketKey, bars };
              return bars;
            });
          }
          setMarketCandlesStatus(
            progress && progress.total > 0
              ? `Loading market history… ${progress.done} / ${progress.total}`
              : "Loading market history…"
          );
        },
      }
    )
      .then(({ candles }) => {
        if (controller.signal.aborted) return;
        // Full window replaces our partial candles — never a Preview's or a backtest's.
        setPreviewCandles((prev) => {
          const take =
            prev.length === 0 ||
            prev === stale ||
            (partialShown && prev.length <= candles.length && !previewLoadingRef.current);
          if (!take) return prev;
          marketCandlesRef.current = { key: marketKey, bars: candles };
          return candles;
        });
        setMarketCandlesStatus(candles.length > 0 ? null : "No candles for this window.");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setMarketCandlesStatus(err instanceof Error ? err.message : "Could not load the chart.");
      })
      .finally(() => {
        if (marketLoadRef.current?.controller === controller) marketLoadRef.current = null;
      });
  }, [candleType, listing, lookbackDays, marketKey, needMarketCandles]);
  useEffect(() => () => marketLoadRef.current?.controller.abort(), []);
  const previewLoadingRef = useRef(previewLoading);
  previewLoadingRef.current = previewLoading;

  useEffect(() => {
    const el = coachHostRef.current;
    if (!el) return;
    const measure = () => coach.setHostWidth(el.getBoundingClientRect().width);
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [coach.setHostWidth, labResearchOpen, coach.panelOpen]);

  const runPreviewRef = useRef(runPreviewConcepts);
  runPreviewRef.current = runPreviewConcepts;
  /** Live preview: a recipe edit (which clears the chart) previews the new recipe after a pause. */
  useEffect(() => {
    if (!livePreview || hasScanned || scanning || previewLoading) return;
    if (!instrumentStepConfirmed || !listing || !setupId.trim() || !recipeFingerprint) return;
    // Not while a saved backtest is opening (its own scan restores the chart) or just after a scan.
    if (scanRulesBlockingRef.current || pendingOpenRef.current || previewSkipRef.current) return;
    if (recipeFingerprint === previewFingerprintRef.current || recipeFingerprint === livePreviewFpRef.current) return;
    const t = window.setTimeout(() => {
      if (pendingOpenRef.current || previewSkipRef.current) return;
      livePreviewFpRef.current = recipeFingerprint;
      void runPreviewRef.current();
    }, 800);
    return () => window.clearTimeout(t);
  }, [livePreview, hasScanned, scanning, previewLoading, instrumentStepConfirmed, listing, setupId, recipeFingerprint]);

  useEffect(() => {
    if (!coachKickPreview) return;
    if (!instrumentStepConfirmed || !setupSlot.id.trim() || !listing) return;
    const t = window.setTimeout(() => {
      void runPreviewConcepts();
      setCoachKickPreview(0);
    }, 0);
    return () => window.clearTimeout(t);
  }, [
    coachKickPreview,
    instrumentStepConfirmed,
    listing,
    runPreviewConcepts,
    setupSlot.id,
  ]);


  /** After Scan, preview-path only moves stop and target. Entry changes are a new Preview. */
  useEffect(() => {
    if (!hasScanned || scanning || matches.length === 0) return;
    if (previewSkipRef.current) return;
    if (!priceRulesReady) return;

    const timer = window.setTimeout(() => {
      previewAbortRef.current?.abort();
      const controller = new AbortController();
      previewAbortRef.current = controller;
      void (async () => {
        try {
          const candles =
            scanCandlesRef.current.length > 0
              ? scanCandlesRef.current
              : previewCandlesRef.current;
          if (candles.length === 0) return;
          const pathTemplate = withResolvedStrategyRdUnitSizes(ruleTemplateRef.current, unitOpts);
          // Send the user's current Max wait: omitting it now means the 24-bar default,
          // not no limit — repricing stop/target must not change which matches fill.
          const pathEntry = strategyRdEntryWithMaxWait(pathTemplate.entry, entrySlotRef.current);
          const previewed = await previewStrategyRdPath(
            {
              matches: matchesRef.current,
              candles: scanJobIdRef.current ? [] : candles,
              scan_job_id: scanJobIdRef.current,
              template: { ...pathTemplate, entry: pathEntry },
            },
            controller.signal
          );
          if (controller.signal.aborted) return;
          setMatches((prior) =>
            mergeStrategyRdPreviewPaths(prior, previewed.matches).map((m) => ({
              ...m,
              direction: strategyRdMatchDisplayDirection(m),
            }))
          );
        } catch (err) {
          if (err instanceof DOMException && err.name === "AbortError") return;
          if (isStrategyRdDisabledError(err)) {
            setDisabled(true);
            return;
          }
          presentMutationError(err, "Could not preview entry path");
        }
      })();
    }, 300);

    return () => {
      window.clearTimeout(timer);
      previewAbortRef.current?.abort();
    };
  }, [
    hasScanned,
    matches.length,
    priceRulesReady,
    ruleTemplate.stop,
    ruleTemplate.target,
    ruleTemplate.atr_length,
    ruleTemplate.pip_size,
    ruleTemplate.tick_size,
    scanCandles.length,
    scanning,
    unitOpts,
  ]);

  const onQuoteVariantChange = useCallback(
    (next: string) => {
      const qualified = strategyRdResolvedSymbolForRequest(quoteCandidates, next) || next;
      setResolvedSymbol(qualified);
      if (scanAfterVariantRef.current) {
        void runScan(qualified);
      } else {
        scanAbortRef.current?.abort();
        const controller = new AbortController();
        scanAbortRef.current = controller;
        void confirmIdentity(symbol.trim().toUpperCase(), qualified, controller).catch((err) => {
          if (err instanceof DOMException && err.name === "AbortError") return;
          if (isStrategyRdQuoteVariantRequired(err)) handleQuoteVariantRequired(err);
        });
      }
    },
    [confirmIdentity, handleQuoteVariantRequired, quoteCandidates, runScan, symbol]
  );

  /** Apply uses the Scan cohort (`matches` + window candles), never the live Preview-only cap. */
  const applyRules = useCallback(async () => {
    const applyCandles = scanCandles.length > 0 ? scanCandles : previewCandles;
    // Never a silent no-op: say why the backtest can't run.
    if (!setupId) {
      toast.message("Pick a setup first", { description: "Open the recipe and choose a setup, then run the backtest." });
      return;
    }
    if (matches.length === 0 || applyCandles.length === 0) {
      toast.message("No setups to backtest yet", { description: "Run backtest again, it finds this window's setups first." });
      return;
    }
    if (!isValidStrategyRdSizing(startingEquity, sizing)) {
      toast.message("Check the account settings", {
        description: "Starting equity and position size must be above zero (Backtest settings → Account).",
      });
      setSettingsOpen(true);
      return;
    }
    scanAbortRef.current?.abort();
    const controller = new AbortController();
    scanAbortRef.current = controller;
    setApplying(true);
    setApplyStatus("Queued…");
    setRuleFieldErrors(null);
    setTradesNote(null);
    setOpenedRobustness(null);
    try {
      let hid = hypothesisId;
      if (!hid) {
        const created = await createStrategyRdHypothesis(
          {
            name: strategyRdHypothesisName(setupId, ruleTemplate),
            scan_context: {
              symbol: listing?.symbol || symbol.trim().toUpperCase(),
              pattern_id: setupId,
              setup_id: setupId,
              setup: compactNestedComposerFields({
                setup: setupSlot,
                confluences: confluenceSlots,
                entry: {
                  mode: entrySlot.mode || ruleTemplate.entry.mode,
                  side: entrySlot.side,
                  candle_type: entrySlot.candle_type,
                },
                setupCandleType: candleType,
              }).setup || {
                id: setupId,
                side: normalizeStrategyRdSlotSide(setupSlot.side) || "both",
              },
              confluences:
                confluenceSlots.length > 0
                  ? strategyRdConfluenceSlotsForRequest(confluenceSlots, candleType)
                  : undefined,
              ...(optionalMinWire ? { optional_min: optionalMinWire } : {}),
              entry: strategyRdEntryWithMaxWait(
                {
                  mode: entrySlot.mode || ruleTemplate.entry.mode,
                  side: normalizeStrategyRdSlotSide(entrySlot.side) || "both",
                  ...(entrySlot.candle_type?.trim()
                    ? { candle_type: entrySlot.candle_type.trim() }
                    : {}),
                },
                entrySlot
              ),
              sr_scopes: strategyRdSrScopesForRequest(srScopes, confluences),
              session_ids: normalizeStrategyRdSessionIdsSelection(sessionIds),
              candle_type: candleType,
              resolved_symbol: listing?.resolved_symbol || resolvedSymbol || undefined,
              asset_class: listing?.asset_class || assetClass.trim() || undefined,
              lookback_days: clampStrategyRdLookbackDays(lookbackDays, candleType),
              ...dateRangeBody,
              // The feed the scan used — Apply's parity label ("same data as TradingView") reads it.
              market_data_broker:
                ohlcStats?.market_data_broker || listing?.market_data_broker || undefined,
              md_source: ohlcStats?.md_source || listing?.md_source || undefined,
            },
            template: withResolvedStrategyRdUnitSizes(
              {
                ...ruleTemplate,
                entry: strategyRdEntryWithMaxWait(ruleTemplate.entry, entrySlot),
              },
              unitOpts
            ),
          },
          controller.signal
        );
        hid = created.id;
        setHypothesisId(hid);
        if (created.report_strategy_hint) {
          setReportStrategyHint(created.report_strategy_hint);
        }
      }
      const applied = await applyStrategyRdHypothesis(
        hid,
        {
          matches: matches.map((m) => ({
            bar_time: m.bar_time,
            direction: m.direction,
            pattern_id: m.pattern_id,
            chart: m.chart,
          })),
          // The server backtests its own copy of the scan when it has one.
          candles: scanJobId ? [] : applyCandles,
          ...(scanJobId ? { scan_job_id: scanJobId } : {}),
          template: withResolvedStrategyRdUnitSizes(
            {
              ...ruleTemplate,
              entry: strategyRdEntryWithMaxWait(ruleTemplate.entry, entrySlot),
            },
            unitOpts
          ),
          starting_equity: startingEquity,
          sizing: strategyRdSizingForWire(sizing),
        },
        controller.signal,
        (progress) => setApplyStatus(progress.message)
      );
      if (controller.signal.aborted) return;
      setApplyResults(applied.results);
      setApplyOverview(applied.overview);
      setAppliedTemplateKey(JSON.stringify(ruleTemplateRef.current));
      setApplyMeta({
        applied_at: applied.applied_at,
        sizing: applied.sizing,
        margin: applied.margin,
        currency: applied.currency,
        parity: applied.parity,
        warnings: applied.warnings ?? null,
      });
      if (applied.report_strategy_hint) {
        setReportStrategyHint(applied.report_strategy_hint);
      }
      setVersionsRefresh((n) => n + 1);
      void loadAllTradesRef.current?.(hid, applied.results.length);
      setConfirmName((prev) =>
        prev.trim()
          ? prev
          : strategyRdConfirmDefaultName({
              patternId: setupId,
              symbol: listing?.resolved_symbol || resolvedSymbol || symbol,
              candleType,
            })
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (isStrategyRdDisabledError(err)) {
        setDisabled(true);
        return;
      }
      const fieldErrors = strategyRdRuleFieldErrors(err);
      if (fieldErrors) {
        setRuleFieldErrors(fieldErrors);
        // Show the message by its field.
        setSettingsOpen(true);
        return;
      }
      if (isStrategyRdHypothesisLimitError(err)) {
        // Nothing is deleted automatically: let the user free space.
        toast.error(err instanceof Error ? err.message : "Your backtest history is full (100).", {
          description: "Delete old backtests to run new ones.",
          action: { label: "Backtest history", onClick: () => setSavedBacktestsOpen(true) },
          duration: 12000,
        });
        return;
      }
      presentMutationError(err, "Could not apply rules");
    } finally {
      if (scanAbortRef.current === controller) {
        setApplying(false);
        setApplyStatus(null);
      }
    }
  }, [
    assetClass,
    candleType,
    confluenceSlots,
    confluences,
    entrySlot,
    hypothesisId,
    listing,
    lookbackDays,
    matches,
    catalog,
    ohlcStats,
    optionalMinWire,
    sessionIds,
    setupId,
    setupSlot,
    resolvedSymbol,
    ruleTemplate,
    scanCandles,
    previewCandles,
    sizing,
    startingEquity,
    srScopes,
    symbol,
    unitOpts,
  ]);

  /** Matches are current when the last scan used this exact recipe and window. */
  const scanIsCurrent =
    hasScanned && matches.length > 0 && previewFingerprintRef.current === recipeFingerprint;
  const runBacktest = useCallback(() => {
    if (applying) {
      toast.message("A backtest is already running", { description: "Its results appear below the chart when done." });
      return;
    }
    if (scanning) {
      // Opening a backtest (or a Preview) scans first: run the backtest right after it.
      applyAfterScanRef.current = true;
      toast.message("Backtest queued", { description: "It runs as soon as the setups in this window are found." });
      return;
    }
    if (scanIsCurrent) {
      void applyRules();
      return;
    }
    applyAfterScanRef.current = true;
    void runScan();
  }, [applyRules, applying, runScan, scanIsCurrent, scanning]);
  useEffect(() => {
    if (!applyToken) return;
    void applyRules();
    // applyRules is the one from the render that holds the new matches.
  }, [applyToken]);

  const sizedApplyReady = strategyRdHasSizedApply({
    startingEquity: applyOverview?.starting_equity ?? startingEquity,
    sizing: applyOverview?.sizing ?? sizing,
    overview: applyOverview,
  });
  const confirmedStrategyId = reportStrategyHint?.report_strategy_id?.trim() || null;
  const confirmedDeepLink =
    reportStrategyHint?.deep_link?.trim() ||
    (confirmedStrategyId ? `/strategies/${confirmedStrategyId}` : null);

  const confirmStrategy = useCallback(async () => {
    if (!hypothesisId || !sizedApplyReady || confirmedStrategyId) return;
    scanAbortRef.current?.abort();
    const controller = new AbortController();
    scanAbortRef.current = controller;
    setConfirming(true);
    setConfirmStatus("Queued…");
    try {
      const name =
        confirmName.trim() ||
        strategyRdConfirmDefaultName({
          patternId: setupId,
          symbol: listing?.resolved_symbol || resolvedSymbol || symbol,
          candleType,
        });
      const confirmed = await confirmStrategyRdHypothesis(
        hypothesisId,
        {
          name,
          set_as_current: true,
        },
        controller.signal,
        (progress) => setConfirmStatus(progress.message)
      );
      if (controller.signal.aborted) return;
      const deepLink =
        confirmed.confirmation?.deep_link?.trim() ||
        confirmed.report_strategy_hint?.deep_link?.trim() ||
        (confirmed.report_strategy_id
          ? `/strategies/${confirmed.report_strategy_id}`
          : null);
      setReportStrategyHint({
        report_strategy_id: confirmed.report_strategy_id,
        deep_link: deepLink,
      });
      const pineInfo = {
        pineNotes: confirmed.pine_notes ?? [],
        chartTimeframe: confirmed.chart_timeframe ?? null,
        scripts: confirmed.pinescripts ?? [],
        robustness: confirmed.robustness ?? null,
        tvSymbol: applyMeta?.parity?.tv_symbol ?? null,
      };
      // The strategy page shows these under the Research sim (not stored on the strategy).
      saveStrategyRdPineExportInfo(confirmed.report_strategy_id, pineInfo);
      const chartHint = strategyRdPineChartTimeframeHint(pineInfo.chartTimeframe);
      setSavedInfo({
        name,
        deepLink,
        notes: [
          chartHint ? `In TradingView: ${chartHint}.` : null,
          (confirmed.pinescripts?.length ?? 0) > 1
            ? "Longs and shorts are two TradingView scripts; copy both from the strategy page."
            : null,
        ].filter((n): n is string => Boolean(n)),
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (isStrategyRdDisabledError(err)) {
        setDisabled(true);
        return;
      }
      const fieldErrors = strategyRdRuleFieldErrors(err);
      if (fieldErrors) setRuleFieldErrors(fieldErrors);
      const missing = strategyRdPineNotTranslatableMissing(err);
      if (missing) {
        // Pine step failed (422) — the strategy was not created.
        toastError(err instanceof Error ? err.message : "This recipe can't be turned into a TradingView script yet.", {
          description: missing.length > 0 ? `Not supported: ${missing.join("; ")}` : undefined,
          duration: 12000,
        });
        return;
      }
      presentMutationError(err, "Could not save as strategy");
    } finally {
      if (scanAbortRef.current === controller) {
        setConfirming(false);
        setConfirmStatus(null);
      }
    }
  }, [
    applyMeta?.parity?.tv_symbol,
    candleType,
    confirmName,
    confirmedStrategyId,
    hypothesisId,
    history,
    listing?.resolved_symbol,
    setupId,
    resolvedSymbol,
    sizedApplyReady,
    symbol,
  ]);

  // §6.2: forex / gold (feed_approximate as a warning) without a robustness
  // result for this backtest → ask to check first. Save still queues the test.
  const needsRobustnessFirst =
    !robustnessResult &&
    (applyMeta?.warnings ?? applyOverview?.warnings ?? []).some(
      (w) => w.code === "feed_approximate" && w.severity === "warning"
    );
  const requestConfirm = useCallback(() => {
    if (needsRobustnessFirst) {
      setCheckFirstOpen(true);
      return;
    }
    void confirmStrategy();
  }, [confirmStrategy, needsRobustnessFirst]);
  const checkRobustness = useCallback(() => {
    setResultsTab("robustness");
    setRobustnessRunToken((n) => n + 1);
  }, []);
  const openSetting = useCallback((field: string) => {
    setSettingsOpen(true);
    setSettingFocus({ field, token: Date.now() });
  }, []);

  useEffect(() => {
    return () => scanAbortRef.current?.abort();
  }, []);

  const statsLine = coverageLabel(ohlcStats);

  const setupConditionPicks = strategyRdSetupConditions(setupSlot);
  const recipeLabel = setupId
    ? [
        strategyRdIsConditionSetup(setupId) && setupConditionPicks.length > 0
          ? setupConditionPicks.map((p) => strategyRdConditionPickLabel(p)).join(" + ")
          : (catalog?.concepts.find((c) => c.id === setupId)?.name || setupId).trim(),
        confluenceSlots.length > 0
          ? `${confluenceSlots.length} confluence${confluenceSlots.length === 1 ? "" : "s"}`
          : null,
        entrySlot.mode === "rule" ? "custom entry" : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;
  const backtestBusy = scanning || applying;
  const backtestBusyLabel = scanning
    ? backfillStatus || "Finding setups…"
    : applyStatus || "Running backtest…";
  // Everything a run depends on. The results on screen were made from `appliedRunKey`: while it
  // still matches, running again would give the same backtest.
  const runKey = useMemo(
    () =>
      JSON.stringify([
        symbol, candleType, lookbackDays, dateRange, sessionIds, srScopes, optionalMin,
        setupSlot, confluenceSlots, entrySlot, ruleTemplate, sizing, startingEquity,
      ]),
    [
      symbol, candleType, lookbackDays, dateRange, sessionIds, srScopes, optionalMin,
      setupSlot, confluenceSlots, entrySlot, ruleTemplate, sizing, startingEquity,
    ]
  );
  const runKeyRef = useRef(runKey);
  runKeyRef.current = runKey;
  const [appliedRunKey, setAppliedRunKey] = useState<string | null>(null);
  useEffect(() => {
    // New results (a run finished, or a backtest was opened): they belong to the settings as they are.
    setAppliedRunKey(applyOverview ? runKeyRef.current : null);
  }, [applyOverview]);
  const runUpToDate = applyOverview != null && applyResults.length > 0 && appliedRunKey === runKey;
  const runDisabledReason = !listing
    ? "Pick an asset first."
    : !setupId
      ? "Build a recipe first: or ask TES on the left."
      : strategyRdConditionSetupBlockingReason(setupSlot) ||
        scanRulesBlocking ||
        strategyRdRulesBlockingReason(ruleStatuses) ||
        (runUpToDate ? "Up to date, change the recipe or a setting to run it again." : null);
  const hasResults = applyResults.length > 0 && applyOverview != null;
  useEffect(() => {
    // The backtest picked in Backtest history is open with its results: now the save dialog.
    if (!pendingSaveId || hypothesisId !== pendingSaveId || !hasResults) return;
    setPendingSaveId(null);
    setSaveOpen(true);
  }, [hasResults, hypothesisId, pendingSaveId]);
  const resultsHasSides = strategyRdHasSideViews(applyOverview);
  useEffect(() => {
    if (!resultsHasSides) setResultsSide("combined");
  }, [resultsHasSides]);
  const resultsSideOverview = strategyRdSideOverview(applyOverview, resultsSide);
  const resultsSideFills = useMemo(
    () => strategyRdSideResults(applyResults, resultsSide).map((row) => row.fill ?? null),
    [applyResults, resultsSide]
  );
  // Notes about how TradingView differs (its feed, how it counts split trades, its bar magnifier)
  // belong with the Pine Script — not on a backtest the user may deploy with us.
  const allResultsWarnings = applyMeta?.warnings ?? applyOverview?.warnings ?? null;
  const resultsWarnings = useMemo(
    () => (allResultsWarnings ? allResultsWarnings.filter((w) => !STRATEGY_RD_TRADINGVIEW_NOTE_CODES.has(w.code)) : null),
    [allResultsWarnings]
  );
  const tradingViewNotes = useMemo(
    () => (allResultsWarnings ?? []).filter((w) => STRATEGY_RD_TRADINGVIEW_NOTE_CODES.has(w.code)),
    [allResultsWarnings]
  );
  const pathIncompleteFromBeCopy = Boolean(
    entryGuidance?.user_message?.trim() ||
      entryGuidance?.advice?.trim() ||
      selectedMatch?.path?.user_message?.trim() ||
      selectedMatch?.path?.advice?.trim()
  );
  const pageAlert =
    entryTfUnavailableReason ||
    (pathIncompleteReason
      ? pathIncompleteFromBeCopy
        ? pathIncompleteReason
        : `Selected match has no entry · ${pathIncompleteReason}`
      : null);
  // Settings edits re-render the whole Lab (chart, results): as transitions the field
  // answers at once and the heavy re-render follows without blocking typing / clicks.
  const onRulesPanelTemplateChange = useCallback(
    (next: StrategyRdRuleTemplate) => startTransition(() => setRuleTemplate(next)),
    []
  );
  const onRulesPanelSizingChange = useCallback(
    (next: StrategyRdSizing) => startTransition(() => setSizing(next)),
    []
  );
  const onRulesPanelEquityChange = useCallback(
    (next: number) => startTransition(() => setStartingEquity(next)),
    []
  );
  const marginCallTimes = useMemo(
    () =>
      applyResults.flatMap((r) =>
        (r.fill?.margin_calls ?? []).map((c) => c.bar_time).filter((t): t is string => typeof t === "string" && t.length > 0)
      ),
    [applyResults]
  );
  const backtestTitle =
    confirmName.trim() ||
    (openedBacktest && openedBacktest.id === hypothesisId ? openedBacktest.name : "") ||
    strategyRdConfirmDefaultName({
      patternId: setupId,
      symbol: listing?.resolved_symbol || resolvedSymbol || symbol,
      candleType,
    });
  const backtestPeriod = (() => {
    const fmt = (iso: string | undefined) => {
      if (!iso) return null;
      const d = new Date(iso);
      return Number.isNaN(d.getTime())
        ? null
        : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
    };
    // Dates of the window in UTC (a range ends at 23:59 UTC: in a later zone that is already
    // the next day). The backtest's own window — the chart may only hold its latest candles.
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    const first =
      str(ohlcStats?.window_first_bar) ?? str(ohlcStats?.window_start) ?? str(windowCandles[0]?.timestamp);
    // A window ending at midnight UTC covers the day before it.
    const endRaw = str(ohlcStats?.window_end) ?? str(windowCandles[windowCandles.length - 1]?.timestamp);
    const endMs = endRaw ? new Date(endRaw).getTime() : Number.NaN;
    const last = Number.isFinite(endMs) ? new Date(endMs - 1000).toISOString() : endRaw;
    return [fmt(first), fmt(last)].filter(Boolean).join(" – ");
  })();
  const pineStale =
    appliedTemplateKey != null && appliedTemplateKey !== JSON.stringify(ruleTemplate);
  const chartTopReserve = 260;

  return (
    <FlexAny
      data-strategy-rd-page=""
      direction="column"
      pt={shadcnInset ? 0 : { base: "100px", md: "80px", lg: "24px" }}
      pb={0}
      px={shadcnInset ? 0 : { base: "12px", xl: "24px" }}
      gap="12px"
      w="100%"
      minW={0}
      flex="1"
      minH={0}
      overflow="hidden"
    >
      {insetHeaderMount
        ? createPortal(
            <StrategyRdLabTopBarActions
              usage={
                coach.usage
                  ? {
                      turnsUsed: coach.usage.turns_used,
                      turnsCap: coach.usage.turns_cap,
                      aiMode: coach.usage.mode ?? null,
                      backtestsUsed: coach.usage.backtests_used ?? null,
                      backtestsCap: coach.usage.backtests_cap ?? null,
                      backtestsWindow: coach.usage.backtests_window ?? null,
                    }
                  : null
              }
              onPreview={hasScanned || !setupId || livePreview ? undefined : () => void runPreviewConcepts()}
              previewBusy={previewLoading}
              previewDisabled={!listing || !setupId || scanning}
              onOpenLibrary={() => setSavedBacktestsOpen(true)}
              onRunBacktest={runBacktest}
              runDisabledReason={runDisabledReason}
              busy={backtestBusy}
              busyLabel={backtestBusyLabel}
            />,
            insetHeaderMount
          )
        : null}
      {presetsLoading ? (
        <Flex flex="1" minH={0} w="100%" align="center" justify="center">
          <StatisticsEmptyState
            variant="loading"
            fillParent
            title="Loading Strategy Lab"
            description="Please wait while we load the Concepts catalog."
          />
        </Flex>
      ) : disabled ? (
        <Flex flex="1" minH={0} w="100%" align="center" justify="center">
          <StatisticsEmptyState
            variant="no-data"
            fillParent
            title="Strategy Lab is not available"
            description="Research is turned off on the server. Ask an admin to enable STRATEGY_RD_ENABLED."
          />
        </Flex>
      ) : (
        <div
          ref={(el) => {
            coachHostRef.current = el;
            labFillRef(el);
          }}
          className="flex h-full min-h-0 w-full min-w-0 overflow-hidden transition-[padding] duration-200"
          style={{ height: labHeight, ...(settingsDrawerWidth ? { paddingRight: settingsDrawerWidth } : {}) }}
          data-strategy-rd-coach-host=""
          data-strategy-rd-lab=""
        >
          {/* TES first, Quant-style: the chat drives the Lab. */}
          {/* Minimized, TES folds to a strip on this edge; the panel stays mounted (hidden), so
              opening it again is instant. */}
          {labResearchOpen && !coach.panelOpen ? (
            <StrategyRdCoachRail onOpen={coach.openPanel} working={coach.sending} />
          ) : null}
          {labResearchOpen ? (
            <div
              className={cn("flex h-full min-w-[300px] shrink flex-col !border-0 !border-solid !border-border !border-r", !coach.panelOpen && "hidden")}
              style={{ width: coach.panelWidth }}
            >
              <CoachPanelSection
                width={coach.panelWidth}
                dock="left"
                onResizeStart={coach.onResizeStart}
                onMinimize={coach.minimizePanel}
                bootstrapping={coach.bootstrapping}
                usage={coach.usage}
                styles={coach.styles}
                threads={coach.threads}
                sessionId={coach.sessionId}
                messages={coach.messages}
                catalog={catalog}
                suggestedChips={coach.suggestedChips}
                pendingProposal={coach.pendingProposal}
                attachments={coach.attachments}
                onAddImages={(files) => void coach.addAttachments(files)}
                onRemoveAttachment={coach.removeAttachment}
                chartFocus={coach.chartFocus}
                onClearChartFocus={coach.clearChartFocus}
                onOpenHypothesis={(id) => void openHypothesis(id)}
                onCompareHypothesis={(id) => {
                  // The Versions tab shows the run next to its original.
                  void openHypothesis(id).then(() => setResultsTab("versions"));
                }}
                timeZone={displayTimeZone}
                input={coach.input}
                onInputChange={coach.setInput}
                sending={coach.sending}
                activity={coach.activity}
                intakeBusy={coach.intakeBusy}
                confirmingPatch={coach.confirmingPatch}
                quotaBlocked={coach.quotaBlocked}
                threadsBusy={coach.threadsBusy}
                pendingRestore={Boolean(coach.pendingRestore)}
                backtestChat={coach.backtestChat}
                openedBacktest={coach.openedBacktest}
                openedBacktestChips={coach.openedBacktestChips}
                intakeOpen={coach.intakeOpen}
                onCloseIntake={coach.closeIntake}
                onStyleClick={coach.buildFromStyle}
                onWow={() => void coach.surpriseMe()}
                onSend={(text) => void coach.sendTurn(text)}
                onConfirmPatch={() => void coach.confirmPatch()}
                onDismissPatch={coach.dismissPatch}
                onSelectThread={(id) => void coach.loadThread(id)}
                onNewThread={() => {
                  resetLabForNewChat();
                  void coach.newThread();
                }}
                onDeleteThread={(id) => void coach.removeThread(id)}
                onConfirmRestore={coach.confirmRestore}
                onDismissRestore={coach.dismissRestore}
                onChipClick={coach.onChipClick}
                disclaimer={coach.disclaimer}
                orientationChips={coach.orientationChips}
                onOrientationChip={(chip) => void coach.onOrientationChip(chip)}
                sessionOptions={sessionPickerGroups}
              />
            </div>
          ) : null}

          {/* Chart top to bottom, always in view: results push it up and scroll on their own. */}
          <div
            // The chart keeps room: panels beside it shrink first.
            className="flex h-full min-w-[420px] flex-1 flex-col overflow-hidden bg-background"
            data-strategy-rd-workspace=""
          >

            {pageAlert ? (
              <div className="flex shrink-0 flex-col gap-2 px-3 pt-3">
                {/* The app's warning alert (the Chakra reset strips a plain one to bare text). */}
                <Alert
                  variant="warning"
                  className="flex w-full items-start gap-3 !rounded-[6px] !border !border-solid !border-amber-500/45 !px-4 !py-3"
                  data-strategy-rd-page-alert=""
                >
                  <span aria-hidden className="flex h-5 w-4 shrink-0 items-center justify-center">
                    <AlertTriangle className="size-4 text-amber-600 dark:text-amber-400" />
                  </span>
                  <AlertDescription className="min-w-0 flex-1 font-[family-name:var(--font-primary)] text-sm font-normal leading-5 !text-gray-500 dark:!text-white">
                    {pageAlert}
                  </AlertDescription>
                </Alert>
              </div>
            ) : null}

            <StrategyRdChartStack
              showResults={Boolean(hasResults && applyOverview)}
              resultsLabel={backtestTitle ? `Backtest results · ${backtestTitle}` : "Backtest results"}
              chart={
              <StrategyRdDetectChart
                fill
                timeZoneLabel={displayTimeZone}
                symbolProvider={chartSymbolProvider}
                allowedCandleTypes={STRATEGY_RD_CANDLE_TYPES}
                onCandleTypeChange={onCandleTypeChange}
                onInstrumentChange={onChartInstrumentChange}
                footerStart={
                  <StrategyRdWindowPicker
                    lookbackDays={lookbackDays}
                    lookbackCap={lookbackCap}
                    onLookbackChange={(days) => {
                      setDateRange(null);
                      setLookbackDays(clampStrategyRdLookbackDays(days, candleType));
                    }}
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    chartFrom={chartFromLabel}
                    candleLabel={candleType}
                    pendingRun={
                      hasScanned &&
                      ohlcStats?.lookback_days != null &&
                      ohlcStats.lookback_days !== clampStrategyRdLookbackDays(lookbackDays, candleType)
                    }
                  />
                }
                legendHidden={legendHidden}
                onLegendHiddenChange={onLegendHiddenChange}
                candleScheme={candleScheme}
                mode={hasScanned ? "scan" : "preview"}
                matches={detectMatches}
                matchMarkers={detectMarkers}
                selectedMatch={selectedMatch}
                entryGuidance={entryGuidance}
                candles={windowCandles}
                candleType={candleType}
                symbolLabel={
                  listing
                    ? listing.resolved_symbol || listing.symbol || resolvedSymbol || null
                    : null
                }
                identityReady={Boolean(listing)}
                previewCommitted={previewCommitted || hasScanned}
                loading={previewLoading || scanning || (windowCandles.length === 0 && marketCandlesStatus === "Loading chart…")}
                loadingLabel={
                  scanning || previewLoading
                    ? backfillStatus || (scanning ? "Finding setups…" : "Loading examples…")
                    : marketCandlesStatus
                }
                timeZone={displayTimeZone}
                sessionLabelFor={sessionLabelForMatch}
                setupId={setupId}
                confluences={confluenceRefsForChart}
                laneBConceptIds={laneBConceptIds}
                laneBRecipe={{
                  setup: setupSlot,
                  confluences: confluenceSlots,
                }}
                sessions={catalog?.sessions?.sessions ?? null}
                srScopes={srScopes}
                srRails={detectSrRails}
                conceptLabel={chartConceptLabel}
                indicatorSeries={
                  // Same response as the chart's candles (windowCandles prefers scan candles).
                  ((scanCandles.length > 0 ? ohlcStats : previewOhlcStats)?.confluences as
                    | { indicator_series?: Record<string, unknown> | null }
                    | null
                    | undefined)?.indicator_series ?? null
                }
                diagnostics={hasScanned ? confluenceSkipDisplay : previewDiagnostics}
                levels={hasScanned ? selectedLevels : null}
                ruleTrades={hasScanned ? scanRuleTrades : null}
                selectedTradeId={selectedKey}
                showPathNotice={false}
                onAskAbout={({ timeMs, tradeId }) => {
                  // On a trade marker: its entry candle, so TES explains that trade.
                  const trade = tradeId ? scanRuleTrades.find((t) => t.trade_id === tradeId) : null;
                  const time = trade?.entry_time || new Date(timeMs).toISOString();
                  coach.askAbout({ time, candle_type: candleType }, trade ? "trade" : "candle");
                }}
                onSelectTrade={(id) => {
                  // Chart click: select only — keep current zoom.
                  if (id) setSelectedKey(id);
                }}
                sessionLabel={selectedMatch ? sessionLabelForMatch(selectedMatch) : null}
                detectCountLabel={strategyRdDetectFilterCountLabel(hasScanned ? ohlcStats : previewOhlcStats)}
                previewDisabled={!listing || !setupId || scanning}
                frameGuideKey={strategyRdDetectWindowKey(windowCandles) || null}
                focusMatchToken={focusMatchToken}
                previewEmpty={previewCommitted && !hasScanned ? previewEmptyState : null}
                onPreviewEmptyAction={(action) => {
                  if (action.id === "ask_tes_zero_hits" || action.goal) {
                    coach.openPanel();
                    void coach.sendTurn(
                      action.id === "ask_tes_zero_hits" ? "Why no matches" : action.label || "Why no matches",
                      action.goal === "explain_zero_hits" ? "why_no_hits" : action.goal || "why_no_hits"
                    );
                    return;
                  }
                  if (action.id === "open_orientation" || action.faq_id) {
                    coach.openPanel();
                    void coach.onOrientationChip({
                      id: action.faq_id || "preview_zero_hits",
                      label: action.label,
                      faq_id: action.faq_id || "preview_zero_hits",
                    });
                    return;
                  }
                  if (action.id === "simplify_composer") {
                    setRecipeOpen(true);
                    toast.message("Simplify the recipe", {
                      description: "Try fewer confluences or looser sessions, then Preview again.",
                    });
                  }
                }}
              />
              }
              results={
            <StrategyRdResultsDock
              maxHeight={Math.max(180, labHeight - chartTopReserve)}
              defaultHeight={Math.round(labHeight * 0.5)}
              header={
                hasResults && applyOverview ? (
                  <StrategyRdLabResultsHeader
                    title={backtestTitle}
                    meta={[
                      backtestPeriod ? { id: "period", label: "Period", value: backtestPeriod, icon: Calendar } : null,
                      hasScanned
                        ? {
                            id: "setups",
                            label: "Setups",
                            value:
                              strategyRdDetectFilterCountLabel(ohlcStats) ||
                              `${matches.length} setup${matches.length === 1 ? "" : "s"}`,
                            icon: Crosshair,
                          }
                        : null,
                      hasScanned && statsLine ? { id: "data", label: "Data", value: statsLine, icon: CandlestickChart } : null,
                      hasScanned && ohlcStats?.lookback_days != null
                        ? { id: "window", label: "Window", value: strategyRdWindowLabel(ohlcStats.lookback_days, true), icon: History }
                        : null,
                      hasScanned && confluenceSkipDisplay.length > 0
                        ? { id: "skipped", label: "Filters skipped", value: confluenceSkipDisplay.join(" · "), icon: Filter }
                        : null,
                    ].filter((m): m is StrategyRdResultsMeta => m != null)}
                    marginCallTimes={marginCallTimes}
                    overview={applyOverview}
                    side={resultsSide}
                    onSideChange={setResultsSide}
                    tab={resultsTab}
                    onTabChange={setResultsTab}
                    currency={applyMeta?.currency?.account ?? null}
                    parity={applyMeta?.parity ?? null}
                    warnings={resultsWarnings}
                    onCheckRobustness={checkRobustness}
                    onOpenSetting={openSetting}
                    onUseCosts={(costs) => {
                      const set = Object.fromEntries(Object.entries(costs).filter(([, v]) => v != null));
                      setSizing((prev) => ({ ...prev, ...set }));
                      setApplyToken((n) => n + 1);
                      toast.message("Typical costs applied", {
                        description: "Running the backtest again with them. Adjust them in Backtest settings.",
                      });
                    }}
                    actionsHint={
                      !confirmedStrategyId && !sizedApplyReady
                        ? "Run the backtest with starting equity and size to save it."
                        : null
                    }
                    actions={
                      <>
                        {/* Right by the results: the stop, target, size and costs behind them can be changed. */}
                        <Button
                          type="button"
                          variant="outline-brand"
                          size="sm"
                          data-font="primary"
                          className={cn(strategyRdBrandActionButtonClassName, "gap-1.5")}
                          disabled={backtestBusy}
                          onClick={() => setSettingsOpen(true)}
                          title="Change the stop, target, trade management, position size, costs or account. Then run it again"
                          data-strategy-rd-tweak-settings=""
                        >
                          <SlidersHorizontal className="size-3.5" aria-hidden />
                          <span className="normal-case tracking-normal">Tweak settings</span>
                        </Button>
                        <Button
                          type="button"
                          variant="outline-brand"
                          size="sm"
                          data-font="primary"
                          className={strategyRdBrandActionButtonClassName}
                          disabled={!hypothesisId || backtestBusy}
                          onClick={() => setLabPanel("pine")}
                          data-strategy-rd-get-pine=""
                        >
                          <span className="normal-case tracking-normal">Get Pine Script</span>
                        </Button>
                        <Button
                          type="button"
                          variant="outline-brand"
                          size="sm"
                          data-font="primary"
                          className={strategyRdBrandActionButtonClassName}
                          disabled={!hypothesisId || backtestBusy}
                          onClick={() => history.push(`/launchpad?deploy=${encodeURIComponent(hypothesisId ?? "")}`)}
                          title="Launch this strategy on Launchpad. On paper or on your broker account"
                          data-strategy-rd-deploy-live=""
                        >
                          <span className="normal-case tracking-normal">Launch</span>
                        </Button>
                        <Button
                          type="button"
                          variant="outline-brand"
                          size="sm"
                          data-font="primary"
                          className={strategyRdBrandActionButtonClassName}
                          disabled={!hypothesisId || backtestBusy || openingReport}
                          onClick={() => hypothesisId && void openReport(confirmedStrategyId, `backtest:${hypothesisId}`)}
                          title="Create a report on this backtest's trades. Its findings can become the strategy's next policy version"
                          data-strategy-rd-report=""
                        >
                          <span className="normal-case tracking-normal">Report on trades</span>
                        </Button>
                        {confirmedStrategyId ? (
                          <Button
                            type="button"
                            variant="gradient"
                            size="sm"
                            data-font="primary"
                            className="h-8"
                            onClick={() => {
                              if (confirmedDeepLink) history.push(confirmedDeepLink);
                            }}
                          >
                            <span className="normal-case tracking-normal">Saved as a strategy · Open</span>
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="gradient"
                            size="sm"
                            data-font="primary"
                            className="h-8"
                            disabled={!sizedApplyReady || !hypothesisId || backtestBusy || confirming}
                            onClick={() => setSaveOpen(true)}
                            data-strategy-rd-confirm-cta=""
                            title="Turns this backtest into a Strategy (Strategies page). Its version 1 is these rules"
                          >
                            {confirming ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
                            <span className="normal-case tracking-normal">
                              {confirming ? confirmStatus || "Saving the strategy…" : "Save as strategy · v1"}
                            </span>
                          </Button>
                        )}
                      </>
                    }
                  />
                ) : null
              }
              emptyState={
                backtestBusy ? (
                  <p className={cn(strategyRdMetaClassName, "flex items-center gap-2")}>
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                    {backtestBusyLabel}
                  </p>
                ) : !setupId ? null : (
                  <StrategyRdDockHint>
                    {detectMatches.length > 0 && !hasScanned
                      ? `${detectMatches.length} example${detectMatches.length === 1 ? "" : "s"} on the chart. Run backtest to test every setup in this window.`
                      : "Run backtest to see how this recipe would have traded."}
                  </StrategyRdDockHint>
                )
              }
            >
              {/* Every tab stays mounted: a running robustness test keeps polling off-tab. */}
              <div className="flex flex-col gap-4">
                <div hidden={resultsTab !== "overview"} data-strategy-rd-results-panel="overview">
                  <StrategyRdWhenVisible active={resultsTab === "overview"}>
                  {applyOverview ? (
                    <LabOverviewSection
                      overview={resultsSideOverview}
                      fullOverview={applyOverview}
                      side={resultsSide}
                      fills={resultsSideFills}
                      currency={applyMeta?.currency?.account ?? null}
                      margin={applyMeta?.margin ?? null}
                      warnings={resultsWarnings}
                      returnRange={
                        robustnessResult?.verdict === "sensitive" && robustnessResult.return_pct
                          ? robustnessResult.return_pct
                          : null
                      }
                  onAskTes={askTes}
                    />
                  ) : null}
                  </StrategyRdWhenVisible>
                </div>
                <div hidden={resultsTab !== "trades"} data-strategy-rd-results-panel="trades">
                  <StrategyRdWhenVisible active={resultsTab === "trades"}>
                  <TradesSection
                    matches={matches}
                    results={applyResults}
                    sideView={resultsSide}
                    selectedKey={selectedKey}
                    timeZone={displayTimeZone}
                    currency={applyMeta?.currency?.account ?? null}
                    tradesNote={tradesNote}
                    sideOverview={applyOverview}
                    onSideChange={setResultsSide}
                    symbol={listing?.resolved_symbol || listing?.symbol || resolvedSymbol || symbol}
                    onSelect={(match) => {
                      const key = strategyRdMatchKey(match);
                      setSelectedKey(key);
                      setFocusMatchToken(`rail:${key}:${Date.now()}`);
                    }}
                  />
                  </StrategyRdWhenVisible>
                </div>
                <div hidden={resultsTab !== "robustness"} data-strategy-rd-results-panel="robustness">
                  <StrategyRdWhenVisible active={resultsTab === "robustness"}>
                  <StrategyRdRobustnessPanel
                    hypothesisId={hypothesisId}
                    appliedAt={applyMeta?.applied_at ?? null}
                    initial={openedRobustness}
                    timeZone={displayTimeZone}
                    currency={applyMeta?.currency?.account ?? null}
                    onFragileTradesChange={setFragileTrades}
                    onResultChange={setRobustnessResult}
                    onAskTes={askTes}
                    runToken={robustnessRunToken}
                    onSelectFragile={(trade) => {
                      const at = Date.parse(trade.entry_time);
                      const hit = Array.from(applyFillByKey.entries()).find(
                        ([, r]) =>
                          (r.fill?.direction || r.direction) === trade.side &&
                          Date.parse(r.fill?.entry_time || r.fill?.bar_time || "") === at
                      )?.[0];
                      if (!hit) return;
                      setSelectedKey(hit);
                      setFocusMatchToken(`rail:${hit}:${Date.now()}`);
                    }}
                  />
                  <div className="mt-6">
                    <StrategyRdMarketsPanel
                      hypothesisId={hypothesisId}
                      appliedAt={applyMeta?.applied_at ?? null}
                      symbol={listing?.resolved_symbol || resolvedSymbol || symbol}
                      initial={openedBacktest?.id === hypothesisId ? openedMarkets : null}
                      available={marketSymbols}
                    />
                  </div>
                  </StrategyRdWhenVisible>
                </div>
                <div hidden={resultsTab !== "account"} data-strategy-rd-results-panel="account">
                  <StrategyRdWhenVisible active={resultsTab === "account"}>
                  <StrategyRdAccountPanel
                    hypothesisId={hypothesisId}
                    appliedAt={applyMeta?.applied_at ?? null}
                    startingEquity={startingEquity}
                    timeZone={displayTimeZone}
                    currency={applyMeta?.currency?.account ?? null}
                    onAskTes={askTes}
                  />
                  </StrategyRdWhenVisible>
                </div>
                <div hidden={resultsTab !== "versions"} data-strategy-rd-results-panel="versions">
                  <StrategyRdWhenVisible active={resultsTab === "versions"}>
                  <StrategyRdVersionsPanel
                    hypothesisId={hypothesisId}
                    refreshToken={versionsRefresh}
                    timeZone={displayTimeZone}
                    onOpen={(id) => void openBacktestWithChat(id, { stayIfUnlinked: true })}
                    onDeleted={(id) => {
                      if (id !== hypothesisId) return;
                      // The open backtest is gone: keep the recipe, drop its results.
                      setHypothesisId(null);
                      setApplyResults([]);
                      setApplyOverview(null);
                      setApplyMeta(null);
                    }}
                    onShowAll={() => setSavedBacktestsOpen(true)}
                  />
                  </StrategyRdWhenVisible>
                </div>
                <ResearchDisclaimerNotice kind="strategy_rd" />
              </div>
            </StrategyRdResultsDock>
              }
            />
          </div>

          {/* Setups, Pine and Chart settings: the same pull-out panel as Strategy settings. */}
          <StrategyRdLabDrawer
            inline
            open={labPanel === "setups" && detectMatches.length > 0}
            onOpenChange={(open) => {
              if (!open) setLabPanel(null);
            }}
            storageKey="setups"
            defaultWidth={360}
            title={hasScanned ? "Setups found" : "Setups on this chart"}
            description={`${detectMatches.length} on ${listing?.resolved_symbol || resolvedSymbol || symbol} · ${candleType}. Click one to jump to it on the chart.`}
            data-strategy-rd-setups-drawer=""
          >
            <div className="flex h-full min-h-0 flex-col p-4">
                <MatchRailSection
                  matches={detectMatches}
                  selectedKey={selectedKey}
                  timeZone={displayTimeZone}
                  onSelect={(row) => {
                    const key = strategyRdMatchKey(row);
                    setSelectedKey(key);
                    setFocusMatchToken(`rail:${key}:${Date.now()}`);
                  }}
                  sessionLabelFor={sessionLabelForMatch}
                  outcomes={matchOutcomes}
                  candles={windowCandles}
                  candleType={candleType}
                  setupId={setupId}
                  confluences={confluenceRefsForChart}
                  levelsByMatchKey={matchThumbLevels}
                  emptyMessage="No setups in this window yet."
                />
            </div>
          </StrategyRdLabDrawer>
          <StrategyRdLabDrawer
            inline
            open={labPanel === "pine"}
            onOpenChange={(open) => {
              if (!open) setLabPanel(null);
            }}
            storageKey="pine"
            defaultWidth={420}
            title="Pine editor"
            description={backtestTitle || "Last backtest"}
            data-strategy-rd-pine-side=""
          >
              <StrategyRdPinePanel
                hypothesisId={hypothesisId}
                stale={pineStale}
                tvSymbol={applyMeta?.parity?.tv_symbol ?? null}
                name={confirmName.trim() || null}
                tradingViewNotes={tradingViewNotes}
              />
          </StrategyRdLabDrawer>
          <StrategyRdLabDrawer
            inline
            open={labPanel === "chart"}
            onOpenChange={(open) => {
              if (!open) setLabPanel(null);
            }}
            storageKey="chart-settings"
            defaultWidth={360}
            title="Chart settings"
            description="How the Lab draws on the chart."
            data-strategy-rd-chart-settings=""
          >
            <div className="flex flex-col">
                <section className="flex flex-col gap-3 !border-0 !border-b !border-solid !border-border px-5 py-4 last:!border-b-0">
                  <p className={statsCardHeadlineClassName}>
                    Display
                  </p>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">Time zone</span>
                    <StrategyRdTimeZoneToggle mode={timeZoneMode} localTimeZone={localTimeZone} onChange={setTimeZoneMode} />
                  </div>
                  {quoteOptions.length > 0 ? (
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">Quote</span>
                      <StrategyRdQuoteSelect options={quoteOptions} value={resolvedSymbol} onChange={onQuoteVariantChange} />
                    </div>
                  ) : null}
                </section>
                <section className="flex flex-col gap-3 !border-0 !border-b !border-solid !border-border px-5 py-4 last:!border-b-0">
                  <p className={statsCardHeadlineClassName}>
                    Candles
                  </p>
                  <StrategyRdCandleColorPicker options={STRATEGY_RD_CANDLE_SCHEMES} value={candleScheme} onChange={onCandleSchemeChange} />
                </section>
                <section className="flex flex-col gap-1 !border-0 !border-b !border-solid !border-border px-5 py-4 last:!border-b-0">
                  <p className={statsCardHeadlineClassName}>
                    Preview
                  </p>
                  <StrategyRdSettingSwitch
                    label="Live preview"
                    hint="Re-preview the chart after every recipe change"
                    checked={livePreview}
                    onChange={onLivePreviewChange}
                    data-strategy-rd-live-preview=""
                  />
                </section>
            </div>
          </StrategyRdLabDrawer>
          {/* Strategy settings: the recipe (what is traded) and the backtest (how it is traded). */}
          <StrategyRdLabDrawer
            inline
            open={settingsPanel != null}
            onOpenChange={(open) => {
              if (!open) setSettingsPanel(null);
            }}
            onWidthChange={setSettingsDrawerWidth}
            storageKey="strategy-settings.v1"
            defaultWidth={720}
            data-strategy-rd-settings-sheet=""
            title="Strategy settings"
            description={
              settingsPanel === "backtest"
                ? "How each setup is traded. Exit, entry, management, size and costs. Defaults work; change only what you need."
                : "Setup, confluences and entry: what the backtest trades. TES can fill this in for you."
            }
            headerExtra={
              <Tabs
                data-trade-log-tabs=""
                value={settingsPanel ?? "recipe"}
                onValueChange={(v) => setSettingsPanel(v as "recipe" | "backtest")}
                className="mt-2 w-fit"
                data-strategy-rd-settings-tabs=""
              >
                <TabsList className="min-w-max">
                  <TabsTrigger value="recipe" data-strategy-rd-settings-tab="recipe">
                    Recipe
                  </TabsTrigger>
                  <TabsTrigger value="backtest" data-strategy-rd-settings-tab="backtest">
                    Backtest
                  </TabsTrigger>
                </TabsList>
              </Tabs>
          }
            footer={
              settingsPanel === "backtest" ? null : (
              <>
                <Button
                  type="button"
                  variant="outline-brand"
                  size="sm"
                  className={cn(strategyRdBrandActionButtonClassName, "normal-case tracking-normal")}
                  disabled={!listing || !setupId || scanning || previewLoading || hasScanned}
                  onClick={() => {
                    setRecipeOpen(false);
                    void runPreviewConcepts();
                  }}
                  title="Show a few examples on the chart"
                >
                  Preview on chart
                </Button>
                <Button
                  type="button"
                  variant="gradient"
                  size="sm"
                  data-font="primary"
                  disabled={backtestBusy || Boolean(runDisabledReason)}
                  title={runDisabledReason || undefined}
                  onClick={() => {
                    setRecipeOpen(false);
                    runBacktest();
                  }}
                >
                  <span className="normal-case tracking-normal">Run backtest</span>
                </Button>
                {runDisabledReason && !backtestBusy ? (
                  <span className={cn(strategyRdMetaClassName, "text-xs")}>{runDisabledReason}</span>
                ) : null}
              </>
              )
            }
          >
            {settingsPanel === "backtest" ? (
              <div className="min-h-full bg-content-canvas p-5">
                <StrategyRdRulesPanel
                  template={ruleTemplate}
                  typicalCosts={applyOverview?.with_realistic_costs?.costs ?? null}
                  onChange={onRulesPanelTemplateChange}
                  startingEquity={startingEquity}
                  onStartingEquityChange={onRulesPanelEquityChange}
                  sizing={sizing}
                  onSizingChange={onRulesPanelSizingChange}
                  onApply={() => {
                    setSettingsOpen(false);
                    runBacktest();
                  }}
                  applying={backtestBusy}
                  applyingLabel={backtestBusyLabel}
                  symbol={unitOpts.symbol}
                  assetClass={unitOpts.assetClass}
                  appliedLeverage={applyMeta?.sizing?.leverage ?? null}
                  appliedTickSize={applyMeta?.sizing?.tick_size_used ?? null}
                  focusRequest={settingFocus}
                  ruleVocabulary={ruleVocabulary}
                  ruleComposer={ruleComposer}
                  onEntryRuleChange={onEntryRuleChange}
                  ruleFieldErrors={ruleFieldErrors}
                  onRuleStatusesChange={setRuleStatuses}
                  disabled={
                    !listing ||
                    !setupId ||
                    !(ruleTemplate.target.value > 0) ||
                    (strategyRdStopOffsetNeedsValue(ruleTemplate.stop.offset.kind) &&
                      !(ruleTemplate.stop.offset.value > 0))
                  }
                  disabledReason={!listing ? "Pick an asset first." : !setupId ? "Build a recipe first." : null}
                />
              </div>
            ) : (
              <div className="flex min-h-full flex-col gap-4 bg-content-canvas p-5">
                {catalog ? (
                  <StrategyRdComposer
                    catalog={catalog}
                    optionalMin={optionalMin}
                    onOptionalMinChange={setOptionalMin}
                    coachFlashToken={composerFlash.token}
                    coachFlashKeys={composerFlash.keys}
                    setupCandleType={candleType}
                    setup={setupSlot}
                    onSetupChange={onSetupChange}
                    confluences={confluenceSlots}
                    onConfluencesChange={(next) => {
                      setConfluenceSlots(next);
                      setActivePackId(null);
                      if (strategyRdHasClearOfOpposingSr(next.map((c) => c.id))) {
                        const alreadyOn = strategyRdHasClearOfOpposingSr(confluenceSlots.map((c) => c.id));
                        if (!alreadyOn) setSrScopes(["week"]);
                      }
                    }}
                    entry={entrySlot}
                    onEntryChange={(next) => {
                      const aligned = strategyRdAlignEntrySideToSetup(setupSlot, next);
                      const policy = strategyRdSidePolicyFromSlotSide(aligned.side);
                      // Picking a preset here turns a custom entry rule off.
                      const keepRule = aligned.mode === "rule";
                      if (!keepRule && aligned.mode) presetEntryModeRef.current = aligned.mode;
                      setEntrySlot(aligned);
                      setSidePolicy(policy);
                      setRuleTemplate((prev) => {
                        const { rule: _drop, ...presetEntry } = prev.entry;
                        return {
                          ...prev,
                          entry: strategyRdEntryWithMaxWait(
                            {
                              ...(keepRule ? prev.entry : presetEntry),
                              mode: (aligned.mode || prev.entry.mode) as typeof prev.entry.mode,
                              side_policy: policy,
                            },
                            aligned
                          ),
                        };
                      });
                    }}
                    srScopes={srScopes}
                    onSrScopesChange={setSrScopes}
                    sessionIds={sessionIds}
                    onSessionIdsChange={setSessionIds}
                    activePackId={activePackId}
                    onPackSelect={onPackSelect}
                    confluenceSkipped={confluenceSkipped}
                    volumeAvailable={volumeAvailable}
                    logicPath={logicPath}
                    collapsed={false}
                  />
                ) : null}
                <ResearchDisclaimerNotice kind="strategy_rd" />
              </div>
            )}
          </StrategyRdLabDrawer>
          <StrategyRdLabTaskbar
            items={[
              {
                // Recipe + Backtest settings, one panel with both tabs.
                id: "settings",
                label: "Strategy settings",
                icon: Settings,
                active: settingsPanel != null,
                onClick: () => {
                  if (settingsPanel) {
                    setSettingsPanel(null);
                    return;
                  }
                  if (lastSettingsTabRef.current === "backtest") setSettingsOpen(true);
                  else setRecipeOpen(true);
                },
              },
              {
                id: "setups",
                label: hasScanned ? `Setups found · ${detectMatches.length}` : `Setups · ${detectMatches.length}`,
                icon: ListIcon,
                active: labPanel === "setups",
                disabled: detectMatches.length === 0,
                hint: "preview or backtest a recipe first",
                onClick: () => toggleLabPanel("setups"),
              },
              {
                id: "pine",
                label: "Pine editor",
                icon: Code2,
                active: labPanel === "pine",
                disabled: !hypothesisId,
                hint: "run a backtest first",
                onClick: () => toggleLabPanel("pine"),
              },
              {
                id: "chart",
                label: "Chart settings",
                icon: ChartCandlestick,
                active: labPanel === "chart",
                onClick: () => toggleLabPanel("chart"),
              },
            ]}
          />
        </div>
      )}


      <StrategyRdSavedBacktestsDialog
        open={savedBacktestsOpen}
        onOpenChange={setSavedBacktestsOpen}
        currentId={hypothesisId}
        timeZone={displayTimeZone}
        onOpen={(id) => void openBacktestWithChat(id)}
        onSaveAsStrategy={(id) => {
          if (id === hypothesisId && hasResults) {
            setSaveOpen(true);
            return;
          }
          setPendingSaveId(id);
          void openHypothesis(id);
        }}
        onDeleted={(id) => {
          setVersionsRefresh((n) => n + 1);
          if (id !== hypothesisId) return;
          setHypothesisId(null);
          setApplyResults([]);
          setApplyOverview(null);
          setApplyMeta(null);
        }}
      />


      {/* Save as strategy. */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-strategy-rd-confirm="">
          <DialogHeader>
            <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">Save as strategy</DialogTitle>
            <DialogDescription>
              This backtest becomes a strategy on your Strategies page, with these rules as version 1 and
              its TradingView script. Its backtest trades stay separate from your journal. Reports on its
              trades add later versions, and any version can go to Launchpad.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="strategy-rd-confirm-name" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Strategy name
            </FieldLabel>
            <Input
              id="strategy-rd-confirm-name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={backtestTitle}
            />
          </Field>
          {!sizedApplyReady ? (
            <p className={strategyRdMetaClassName} data-strategy-rd-save-needs-sizing="">
              Run the backtest with starting equity and size before saving.
            </p>
          ) : null}
          {confluenceSlots.some((slot) => strategyRdWaveCIsRsId(slot.id)) ? (
            <p className={strategyRdMetaClassName}>
              TradingView: stocks get an editable “RS benchmark” input set to the Lab’s benchmark;
              FX pairs build the same peer basket from the major currency pairs.
            </p>
          ) : null}
          <ResearchDisclaimerNotice kind="strategy_rd" />
          <DialogFooter>
            <Button type="button" variant="outline-gray" size="sm" data-font="primary" onClick={() => setSaveOpen(false)}>
              <span className="normal-case tracking-normal">Cancel</span>
            </Button>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              disabled={!sizedApplyReady || confirming || !hypothesisId}
              onClick={() => {
                setSaveOpen(false);
                requestConfirm();
              }}
            >
              {confirming ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              <span className="normal-case tracking-normal">
                {confirming ? confirmStatus || "Saving…" : "Save as strategy"}
              </span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={savedInfo != null} onOpenChange={(open) => (open ? null : setSavedInfo(null))}>
        <DialogContent data-strategy-rd-saved="">
          <DialogHeader>
            <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">Saved as “{savedInfo?.name}”</DialogTitle>
            <DialogDescription>
              It&apos;s in your Strategies with its TradingView script. Next: a report on its trades finds what
              to filter (its findings become the next policy version), or launch it on paper. You can also keep
              working on this backtest here.
              {savedInfo?.notes?.length ? <span className="mt-2 block">{savedInfo.notes.join(" ")}</span> : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-font="primary"
              disabled={!savedInfo?.deepLink}
              onClick={() => {
                const link = savedInfo?.deepLink;
                setSavedInfo(null);
                if (link) history.push(link);
              }}
            >
              <span className="normal-case tracking-normal">Open the strategy</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-font="primary"
              disabled={!hypothesisId || openingReport}
              onClick={() => {
                setSavedInfo(null);
                if (hypothesisId) void openReport(confirmedStrategyId, `backtest:${hypothesisId}`);
              }}
            >
              <span className="normal-case tracking-normal">Report on its trades</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-font="primary"
              disabled={!confirmedStrategyId}
              onClick={() => {
                setSavedInfo(null);
                if (confirmedStrategyId) history.push(`/launchpad?deploy_strategy=${encodeURIComponent(confirmedStrategyId)}`);
              }}
            >
              <span className="normal-case tracking-normal">Launch</span>
            </Button>
            <Button type="button" variant="gradient" size="sm" data-font="primary" onClick={() => setSavedInfo(null)}>
              <span className="normal-case tracking-normal">Keep working in the Lab</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={checkFirstOpen} onOpenChange={setCheckFirstOpen}>
        <DialogContent data-strategy-rd-check-first="">
          <DialogHeader>
            <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">Check this recipe against feed differences first?</DialogTitle>
            <DialogDescription>
              Forex and gold prices differ between feeds, TradingView&apos;s included, so this
              backtest is one of many possible outcomes. The robustness test shows the range (about
              20 seconds).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-font="primary"
              onClick={() => {
                setCheckFirstOpen(false);
                void confirmStrategy();
              }}
            >
              <span className="normal-case tracking-normal">Save anyway</span>
            </Button>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              onClick={() => {
                setCheckFirstOpen(false);
                checkRobustness();
              }}
            >
              <span className="normal-case tracking-normal">Check robustness</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </FlexAny>
  );
}
